import React, { useEffect, useState, useRef, useMemo, useCallback } from 'react';
import { Plus, Edit, Trash2, MessageSquare, X, Send, Calendar, User, CheckCircle, Clock, AlertCircle, Smile, Reply, Paperclip, File as FileIcon, Download, Mic, Square, GitBranch, Network, Eye, MapPin, Loader2 } from 'lucide-react';
import { taskApi, employeeApi, positionApi, scheduleApi, subTaskApi, getApiBaseUrl, getApiBaseUrlNoPath, getProfileImageUrl, getUploadedFileUrl, getTaskConversationAttachmentPlaybackUrl, getSubTaskConversationAttachmentPlaybackUrl } from '../services/api';
import { Task, CreateTaskRequest, UpdateTaskRequest, TaskConversation, Employee, TaskConversationReaction, Position, Schedule, SubTask, CreateSubTaskRequest, UpdateSubTaskRequest, SubTaskConversation } from '../types';
import { format } from 'date-fns';
import { formatCompanyDate, formatCompanyDateTime } from '../utils/companyDateFormat';
import { useAuth } from '../context/AuthContext';
import { useLocation, useNavigate } from 'react-router-dom';
import { useLocationContext } from '../components/Layout';
import InactiveLocationBanner from '../components/InactiveLocationBanner';
import { usePermissions } from '../hooks/usePermissions';
import * as signalR from '@microsoft/signalr';
import BasketSelect from '../components/BasketSelect';
import { ConversationVoiceAttachment } from '../components/ConversationVoiceAttachment';
import AssignmentCircleLink from '../components/AssignmentCircleLink';
import {
  filterEmployeesByPositionAssignment,
  filterEmployeesByScheduleAssignment,
} from '../utils/taskAssignmentEmployees';
import {
  extractAssignmentFormIds,
  employeeBasketExtras,
  positionBasketExtras,
  scheduleBasketExtras,
} from '../utils/taskAssignmentForm';
import {
  buildAssignmentNameMaps,
  detectInactiveAssignees,
  hasAnyAssignmentIds,
  stripInactiveAssignees,
  type InactiveAssignee,
  type InactiveResolution,
} from '../utils/taskInactiveAssignees';
import InactiveAssigneesDialog from '../components/InactiveAssigneesDialog';

const TaskAdmin: React.FC = () => {
  const [tasks, setTasks] = useState<Task[]>([]);
  const [employees, setEmployees] = useState<Employee[]>([]);
  const [assignmentEmployeesModal, setAssignmentEmployeesModal] = useState<{
    kind: 'position' | 'schedule';
    id: number;
    label: string;
    employees: Employee[];
    loading: boolean;
  } | null>(null);
  const [positions, setPositions] = useState<Position[]>([]);
  const [schedules, setSchedules] = useState<Schedule[]>([]);
  const [assignedToCategory, setAssignedToCategory] = useState<'employees' | 'positions' | 'schedules'>('employees');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [editingTask, setEditingTask] = useState<Task | null>(null);
  const [selectedTask, setSelectedTask] = useState<Task | null>(null);
  const [showChat, setShowChat] = useState(false);
  const [newMessage, setNewMessage] = useState('');
  const [sendingMessage, setSendingMessage] = useState(false);
  const [loadingConversations, setLoadingConversations] = useState(false);
  const [replyingTo, setReplyingTo] = useState<TaskConversation | null>(null);
  const [showReactionPicker, setShowReactionPicker] = useState<number | null>(null);
  const [subTasks, setSubTasks] = useState<SubTask[]>([]);
  const [selectedSubTask, setSelectedSubTask] = useState<SubTask | null>(null);
  const [showSubTaskChat, setShowSubTaskChat] = useState(false);
  const [showSubTaskForm, setShowSubTaskForm] = useState(false);
  const [editingSubTask, setEditingSubTask] = useState<SubTask | null>(null);
  const [subTaskFormData, setSubTaskFormData] = useState<CreateSubTaskRequest>({
    name: '',
    description: '',
    assignedToEmployeeIds: [],
    assignedToPositionIds: [],
    assignedToScheduleIds: [],
    dueDate: format(new Date(), 'yyyy-MM-dd'),
    status: 'Not Started',
    completedDate: '',
  });
  const [subTaskAssignedToCategory, setSubTaskAssignedToCategory] = useState<'employees' | 'positions' | 'schedules'>('employees');
  const [showTaskView, setShowTaskView] = useState(false);
  const [viewingTask, setViewingTask] = useState<Task | null>(null);
  const [viewingSubTasks, setViewingSubTasks] = useState<SubTask[]>([]);
  const [showSubTaskView, setShowSubTaskView] = useState(false);
  const [viewingSubTask, setViewingSubTask] = useState<SubTask | null>(null);
  const [newSubTaskMessage, setNewSubTaskMessage] = useState('');
  const [sendingSubTaskMessage, setSendingSubTaskMessage] = useState(false);
  const [replyingToSubTask, setReplyingToSubTask] = useState<SubTaskConversation | null>(null);
  const [selectedSubTaskFiles, setSelectedSubTaskFiles] = useState<File[]>([]);
  const [isRecordingSubTask, setIsRecordingSubTask] = useState(false);
  const [recordingTimeSubTask, setRecordingTimeSubTask] = useState(0);
  const [showSubTaskReactionPicker, setShowSubTaskReactionPicker] = useState<number | null>(null);
  const [showReactionDetails, setShowReactionDetails] = useState<{ conversationId: number; reaction: TaskConversationReaction; isSubTask: boolean } | null>(null);
  const [mentionQuery, setMentionQuery] = useState('');
  const [showMentionList, setShowMentionList] = useState(false);
  const [mentionPosition, setMentionPosition] = useState({ top: 0, left: 0 });
  const messageInputRef = useRef<HTMLInputElement>(null);
  const subTaskMessageInputRef = useRef<HTMLInputElement>(null);
  const conversationRefs = useRef<{ [key: number]: HTMLDivElement | null }>({});
  const subTaskConversationRefs = useRef<{ [key: number]: HTMLDivElement | null }>({});
  const chatScrollContainerRef = useRef<HTMLDivElement>(null);
  const subTaskChatScrollContainerRef = useRef<HTMLDivElement>(null);
  const mediaRecorderRefSubTask = useRef<MediaRecorder | null>(null);
  const audioChunksRefSubTask = useRef<Blob[]>([]);
  const recordingTimerRefSubTask = useRef<NodeJS.Timeout | null>(null);
  const subTaskChatEndRef = useRef<HTMLDivElement>(null);
  const subTaskFileInputRef = useRef<HTMLInputElement>(null);
  const [selectedFiles, setSelectedFiles] = useState<File[]>([]);
  const [taskFormFiles, setTaskFormFiles] = useState<File[]>([]);
  const [subTaskFormFiles, setSubTaskFormFiles] = useState<File[]>([]);
  const [existingTaskAttachments, setExistingTaskAttachments] = useState<any[]>([]);
  const [attachmentsToDelete, setAttachmentsToDelete] = useState<number[]>([]);
  const [existingSubTaskAttachments, setExistingSubTaskAttachments] = useState<any[]>([]);
  const [subTaskAttachmentsToDelete, setSubTaskAttachmentsToDelete] = useState<number[]>([]);
  const [isRecording, setIsRecording] = useState(false);
  const [recordingTime, setRecordingTime] = useState(0);
  const chatEndRef = useRef<HTMLDivElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const audioChunksRef = useRef<Blob[]>([]);
  const recordingTimerRef = useRef<NodeJS.Timeout | null>(null);
  const loadingConversationsRef = useRef(false);
  const signalRConnectionRef = useRef<signalR.HubConnection | null>(null);
  const { employee: currentEmployee } = useAuth();
  const { debouncedSelectedLocationId, selectedLocationId, locations, isSelectedLocationInactive, selectedLocation } = useLocationContext();
  const location = useLocation();
  const navigate = useNavigate();
  const { loading: permissionsLoading, hasTaskManagement, hasSuperAdminRole } = usePermissions();
  const navState = location.state as { taskId?: number; canEditTask?: boolean; canEditSubTask?: boolean; isLimitedTask?: boolean; isLimitedSubTask?: boolean } | null;
  /** Persist My Tasks deep-link flags across history.replaceState (managers only). */
  const myTasksNavRef = useRef<typeof navState>(null);
  if (navState?.taskId && (!myTasksNavRef.current || myTasksNavRef.current.taskId !== navState.taskId)) {
    myTasksNavRef.current = navState;
  }
  const [canEditTask, setCanEditTask] = useState(true);
  const [canEditSubTask, setCanEditSubTask] = useState(true);
  const [isLimitedTask, setIsLimitedTask] = useState(false);
  const [isLimitedSubTask, setIsLimitedSubTask] = useState(false);
  const [myTasksTaskId, setMyTasksTaskId] = useState<number | null>(null);
  const [formData, setFormData] = useState<CreateTaskRequest>({
    name: '',
    description: '',
    locationId: null,
    assignedToEmployeeIds: [],
    assignedToPositionIds: [],
    assignedToScheduleIds: [],
    dueDate: format(new Date(), 'yyyy-MM-dd'),
    status: 'Not Started',
    completedDate: '',
  });

  // Full Task Admin list requires task_management; assigned users may open only via My Tasks deep link (? state.taskId)
  useEffect(() => {
    if (permissionsLoading) return;
    if (hasTaskManagement) return;
    const tid = navState?.taskId ?? myTasksNavRef.current?.taskId;
    if (!tid) {
      navigate('/', { replace: true });
    }
  }, [permissionsLoading, hasTaskManagement, navigate, navState?.taskId]);

  // Load tasks: full list for managers (by location); single task for assignees opening from My Tasks
  useEffect(() => {
    const load = async () => {
      if (permissionsLoading) return;

      if (hasTaskManagement) {
        if (!debouncedSelectedLocationId) {
          setTasks([]);
          setLoading(false);
          return;
        }
        try {
          setLoading(true);
          const tasksData = await taskApi.getAll({ locationId: debouncedSelectedLocationId });
          setTasks(tasksData);
        } catch (error) {
          console.error('Error loading tasks:', error);
          alert('Failed to load tasks. Please try again.');
        } finally {
          setLoading(false);
        }
        return;
      }

      const tid = navState?.taskId ?? myTasksNavRef.current?.taskId;
      if (!tid) {
        setTasks([]);
        setLoading(false);
        return;
      }
      try {
        setLoading(true);
        const task = await taskApi.getById(tid);
        setTasks([task]);
      } catch (error) {
        console.error('Error loading task:', error);
        alert('Failed to load task. Please open it again from My Tasks.');
        navigate('/my-tasks', { replace: true });
      } finally {
        setLoading(false);
      }
    };

    load();
  }, [permissionsLoading, hasTaskManagement, debouncedSelectedLocationId, navigate, navState?.taskId]);

  // Open task from My Tasks deep link once it appears in `tasks`
  useEffect(() => {
    const link = myTasksNavRef.current ?? navState;
    const tid = link?.taskId;
    if (!tid || tasks.length === 0) return;
    const task = tasks.find(t => t.id === tid);
    if (!task) return;
    if (showTaskView && viewingTask?.id === tid) return;

    setMyTasksTaskId(tid);
    setCanEditTask(link?.canEditTask ?? true);
    setCanEditSubTask(link?.canEditSubTask ?? true);
    setIsLimitedTask(link?.isLimitedTask ?? false);
    setIsLimitedSubTask(link?.isLimitedSubTask ?? false);
    setSelectedTask(task);
    setShowTaskView(true);
    setViewingTask(task);
    setViewingSubTasks([]);
    subTaskApi.getAll(task.id).then(setViewingSubTasks).catch(() => {});
    if (hasTaskManagement) {
      window.history.replaceState({}, document.title, window.location.pathname);
      myTasksNavRef.current = null;
    }
  }, [navState?.taskId, tasks, hasTaskManagement, showTaskView, viewingTask?.id]);

  const lookupsLoadedRef = useRef(false);

  // Lazy load employees, positions, schedules when needed (forms, chat, view modal)
  useEffect(() => {
    if (!showForm && !showSubTaskForm && !selectedTask && !showTaskView && !showSubTaskView) return;
    if (lookupsLoadedRef.current) return;
    lookupsLoadedRef.current = true;
    Promise.all([
      employeeApi.getAll(),
      positionApi.getAll(),
      scheduleApi.getAll()
    ]).then(([employeesData, positionsData, schedulesData]) => {
      setEmployees(employeesData.filter((e: Employee) => e.isActive));
      setPositions(positionsData.filter((p: Position) => p.isActive));
      setSchedules(schedulesData.filter((s: Schedule) => s.isActive));
    }).catch((error) => {
      console.error('Error loading lookups:', error);
      lookupsLoadedRef.current = false;
    });
  }, [showForm, showSubTaskForm, selectedTask, showChat, showSubTaskChat, showTaskView, showSubTaskView]);

  const openAssignmentEmployeesModal = useCallback(
    async (kind: 'position' | 'schedule', id: number, label: string, taskLocationId?: number | null) => {
      setAssignmentEmployeesModal({ kind, id, label, employees: [], loading: true });
      try {
        let list: Employee[] = employees.filter((e) => e.isActive);
        if (list.length === 0) {
          const data = await employeeApi.getAll();
          list = (data || []).filter((e: Employee) => e.isActive);
          setEmployees(list);
        }
        const filtered =
          kind === 'position'
            ? filterEmployeesByPositionAssignment(list, id, taskLocationId)
            : filterEmployeesByScheduleAssignment(list, id, taskLocationId);
        setAssignmentEmployeesModal((prev) => {
          if (!prev || prev.kind !== kind || prev.id !== id) return prev;
          return { ...prev, employees: filtered, loading: false };
        });
      } catch {
        setAssignmentEmployeesModal((prev) => {
          if (!prev || prev.kind !== kind || prev.id !== id) return prev;
          return { ...prev, employees: [], loading: false };
        });
      }
    },
    [employees]
  );

  // Effective location for filtering: selected location, task's location when editing, or parent task's location for sub-tasks
  const effectiveLocationId = selectedLocationId ?? editingTask?.locationId ?? selectedTask?.locationId ?? formData.locationId ?? null;

  // Employees whose main or sub location matches the effective location (for assignment dropdown)
  const employeesFilteredByLocation = useMemo(() => {
    if (!effectiveLocationId) return employees;
    return employees.filter((e) => {
      const mainId = e.profile?.mainLocationId;
      const subIds = e.profile?.subLocationIds || [];
      return mainId === effectiveLocationId || subIds.includes(effectiveLocationId);
    });
  }, [employees, effectiveLocationId]);

  // Schedules that have the effective location turned on (ScheduleLocations)
  const schedulesFilteredByLocation = useMemo(() => {
    if (!effectiveLocationId) return schedules;
    return schedules.filter((s) => {
      const locIds = s.locationIds || [];
      return locIds.includes(effectiveLocationId);
    });
  }, [schedules, effectiveLocationId]);

  const [inactiveAssigneesDialog, setInactiveAssigneesDialog] = useState<{
    inactive: InactiveAssignee[];
    resolve: ((choice: InactiveResolution) => void) | null;
  } | null>(null);

  const promptInactiveAssigneesResolution = useCallback((inactive: InactiveAssignee[]) => {
    return new Promise<InactiveResolution>((resolve) => {
      setInactiveAssigneesDialog({ inactive, resolve });
    });
  }, []);

  const idInList = (id: number, list: number[]) => {
    for (let i = 0; i < list.length; i++) {
      if (list[i] === id) return true;
    }
    return false;
  };

  const normalizeAssignmentIds = (ids: number[] | undefined, allowed: number[]) => {
    const out: number[] = [];
    const source = ids ?? [];
    for (let i = 0; i < source.length; i++) {
      const id = Number(source[i]);
      if (!Number.isFinite(id) || !idInList(id, allowed) || idInList(id, out)) continue;
      out.push(id);
    }
    return out;
  };

  const getNormalizedAssignments = useCallback(
    (
      formEmployeeIds: number[],
      formPositionIds: number[],
      formScheduleIds: number[],
      locationId: number | null | undefined
    ) => {
      const schedulesForLoc =
        locationId == null
          ? schedules
          : schedules.filter((s) => {
              const locIds = s.locationIds || [];
              return locIds.includes(locationId);
            });
      const employeesAllowed = employees.map((e) => Number(e.id)).filter((n) => Number.isFinite(n));
      const positionsAllowed = positions.map((p) => Number(p.id)).filter((n) => Number.isFinite(n));
      const schedulesAllowed = schedulesForLoc.map((s) => Number(s.id)).filter((n) => Number.isFinite(n));
      return {
        employeeIds: normalizeAssignmentIds(formEmployeeIds, employeesAllowed),
        positionIds: normalizeAssignmentIds(formPositionIds, positionsAllowed),
        scheduleIds: normalizeAssignmentIds(formScheduleIds, schedulesAllowed),
      };
    },
    [employees, positions, schedules]
  );

  useEffect(() => {
    // Only scroll when conversations change, not when loading
    // But skip auto-scroll if we just sent a message (handled manually)
    if (selectedTask?.conversations && selectedTask.conversations.length > 0 && !sendingMessage) {
      setTimeout(() => {
        if (chatScrollContainerRef.current) {
          chatScrollContainerRef.current.scrollTop = chatScrollContainerRef.current.scrollHeight;
        } else {
          scrollToBottom();
        }
      }, 100);
    }
  }, [selectedTask?.conversations?.length]);

  const scrollToBottom = () => {
    chatEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  // SignalR connection for real-time conversation updates
  useEffect(() => {
    const getHubUrl = () => {
      const apiBaseUrl = getApiBaseUrl();
      const hubUrl = apiBaseUrl.endsWith('/api')
        ? apiBaseUrl.replace('/api', '/hubs/taskconversation')
        : `${apiBaseUrl.replace(/\/$/, '')}/hubs/taskconversation`;
      return hubUrl;
    };

    const hubUrl = getHubUrl();
    const connection = new signalR.HubConnectionBuilder()
      .withUrl(hubUrl, {
        skipNegotiation: false,
        transport: signalR.HttpTransportType.WebSockets | signalR.HttpTransportType.LongPolling,
      })
      .withAutomaticReconnect({
        nextRetryDelayInMilliseconds: retryContext => {
          if (retryContext.elapsedMilliseconds < 60000) {
            return 2000;
          } else {
            return null;
          }
        }
      })
      .build();

    signalRConnectionRef.current = connection;

    // Listen for new sub-task conversations
    connection.on('NewConversation', (conversation: any) => {
      // Check if it's a sub-task conversation (has subTaskId property)
      if (conversation.subTaskId !== undefined) {
        console.log('[SignalR] 📨 NewConversation received for sub-task:', conversation);
        setSelectedSubTask(prev => {
          if (!prev || conversation.subTaskId !== prev.id) {
            return prev;
          }
          const exists = prev.conversations?.some(c => c.id === conversation.id);
          if (exists) return prev;
          return {
            ...prev,
            conversations: [...(prev.conversations || []), conversation as SubTaskConversation]
          };
        });
        // Scroll to bottom
        setTimeout(() => {
          subTaskChatEndRef.current?.scrollIntoView({ behavior: 'smooth' });
        }, 100);
        return; // Don't process as task conversation
      }
    });

    // Listen for new messages (task conversations)
    connection.on('NewMessage', (conversation: TaskConversation) => {
      console.log('[SignalR] 📨 NewMessage received:', conversation);
      console.log('[SignalR] Conversation ID:', conversation.id);
      console.log('[SignalR] Task ID:', conversation.taskId);
      console.log('[SignalR] Message:', conversation.message?.substring(0, 50) || '(empty)');
      console.log('[SignalR] Has attachments property:', 'attachments' in conversation);
      console.log('[SignalR] Attachments value:', conversation.attachments);
      console.log('[SignalR] Attachments type:', typeof conversation.attachments);
      console.log('[SignalR] Attachments is array:', Array.isArray(conversation.attachments));
      console.log('[SignalR] Attachments count:', conversation.attachments?.length || 0);
      
      if (conversation.attachments && conversation.attachments.length > 0) {
        console.log('[SignalR] ✅ Attachments found:');
        conversation.attachments.forEach((att: any, idx: number) => {
          console.log(`[SignalR]   Attachment ${idx + 1}:`, {
            id: att.id,
            fileName: att.fileName,
            filePath: att.filePath,
            fileSize: att.fileSize,
            contentType: att.contentType
          });
        });
      } else {
        console.warn('[SignalR] ⚠️ No attachments in SignalR message!');
      }
      
      setSelectedTask(prev => {
        if (!prev || conversation.taskId !== prev.id) {
          console.log('[SignalR] Not updating selectedTask - wrong task or no selected task');
          return prev;
        }
        // Check if conversation already exists (avoid duplicates)
        const exists = prev.conversations?.some(c => c.id === conversation.id);
        if (exists) {
          console.log('[SignalR] Conversation already exists, skipping');
          return prev;
        }
        console.log('[SignalR] Adding conversation to selectedTask');
        const updatedConversations = [...(prev.conversations || []), conversation];
        return {
          ...prev,
          conversations: updatedConversations,
          conversationsCount: updatedConversations.length
        };
      });
      setTasks(prev => prev.map(t => {
        if (t.id !== conversation.taskId) return t;
        const exists = t.conversations?.some(c => c.id === conversation.id);
        if (exists) return t;
        console.log('[SignalR] Adding conversation to tasks list');
        const updatedConversations = [...(t.conversations || []), conversation];
        return { 
          ...t, 
          conversations: updatedConversations,
          conversationsCount: updatedConversations.length
        };
      }));
    });

    // Listen for reaction updates (both task and sub-task)
    connection.on('ReactionUpdated', (data: { conversationId: number; reactions: TaskConversationReaction[]; subTaskId?: number }) => {
      // Check if it's for a sub-task
      if (data.subTaskId !== undefined) {
        setSelectedSubTask(prev => {
          if (!prev || data.subTaskId !== prev.id) return prev;
          return {
            ...prev,
            conversations: prev.conversations?.map(conv => 
              conv.id === data.conversationId
                ? { ...conv, reactions: data.reactions }
                : conv
            ) || []
          };
        });
        return; // Don't process as task reaction
      }
      
      // Handle as task reaction
      setSelectedTask(prev => {
        if (!prev) return prev;
        return {
          ...prev,
          conversations: prev.conversations?.map(conv => 
            conv.id === data.conversationId
              ? { ...conv, reactions: data.reactions }
              : conv
          ) || []
        };
      });
      setTasks(prev => prev.map(t => ({
        ...t,
        conversations: t.conversations?.map(conv => 
          conv.id === data.conversationId
            ? { ...conv, reactions: data.reactions }
            : conv
        ) || []
      })));
    });

    connection.start()
      .then(() => {
        console.log('SignalR connected to TaskConversationHub');
      })
      .catch((error) => {
        console.error('SignalR connection error:', error);
      });

    return () => {
      connection.stop().catch(() => {});
    };
  }, []);

  // Load sub-tasks when task is selected
  useEffect(() => {
    if (selectedTask) {
      loadSubTasks(selectedTask.id);
    } else {
      setSubTasks([]);
    }
  }, [selectedTask?.id]);

  // Join/leave task group when selected task changes
  useEffect(() => {
    const connection = signalRConnectionRef.current;
    if (!connection || connection.state !== signalR.HubConnectionState.Connected) return;

    if (selectedTask && showChat) {
      connection.invoke('JoinTaskGroup', selectedTask.id).catch((err: any) => {
        console.error('Error joining task group:', err);
      });
    }

    return () => {
      if (selectedTask && connection.state === signalR.HubConnectionState.Connected) {
        connection.invoke('LeaveTaskGroup', selectedTask.id).catch(() => {});
      }
    };
  }, [selectedTask?.id, showChat]);

  // Join/leave sub-task group when selected sub-task changes
  useEffect(() => {
    const connection = signalRConnectionRef.current;
    if (!connection || connection.state !== signalR.HubConnectionState.Connected) return;

    if (selectedSubTask && showSubTaskChat) {
      connection.invoke('JoinTaskGroup', `subtask-${selectedSubTask.id}`).catch((err: any) => {
        console.error('Error joining sub-task group:', err);
      });
    }

    return () => {
      if (selectedSubTask && connection.state === signalR.HubConnectionState.Connected) {
        connection.invoke('LeaveTaskGroup', `subtask-${selectedSubTask.id}`).catch(() => {});
      }
    };
  }, [selectedSubTask?.id, showSubTaskChat]);

  // Auto-scroll sub-task chat to bottom when conversations update
  useEffect(() => {
    if (selectedSubTask?.conversations && selectedSubTask.conversations.length > 0 && !sendingSubTaskMessage) {
      setTimeout(() => {
        if (subTaskChatScrollContainerRef.current) {
          subTaskChatScrollContainerRef.current.scrollTop = subTaskChatScrollContainerRef.current.scrollHeight;
        } else if (subTaskChatEndRef.current) {
          subTaskChatEndRef.current.scrollIntoView({ behavior: 'smooth', block: 'end' });
        }
      }, 100);
    }
  }, [selectedSubTask?.conversations?.length]);

  // Close mention list when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (showMentionList) {
        const target = event.target as HTMLElement;
        if (!target.closest('.mention-autocomplete') && !target.closest('input[type="text"]')) {
          setShowMentionList(false);
          setMentionQuery('');
        }
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showMentionList]);

  // Cleanup: Stop recording when component unmounts
  useEffect(() => {
    return () => {
      if (mediaRecorderRef.current && mediaRecorderRef.current.state !== 'inactive') {
        try {
          mediaRecorderRef.current.stop();
        } catch (e) {
          // Ignore errors when stopping
        }
      }
      if (recordingTimerRef.current) {
        clearInterval(recordingTimerRef.current);
      }
      if (mediaRecorderRefSubTask.current && mediaRecorderRefSubTask.current.state !== 'inactive') {
        try {
          mediaRecorderRefSubTask.current.stop();
        } catch (e) {
          // Ignore errors when stopping
        }
      }
      if (recordingTimerRefSubTask.current) {
        clearInterval(recordingTimerRefSubTask.current);
      }
    };
  }, []);

  const loadConversations = async (taskId?: number) => {
    const idToLoad = taskId || selectedTask?.id;
    if (!idToLoad || loadingConversationsRef.current) {
      console.log('[Load Conversations] Skipping - no task ID or already loading');
      return;
    }
    
    console.log('[Load Conversations] Starting load for task ID:', idToLoad);
    loadingConversationsRef.current = true;
    setLoadingConversations(true);
    try {
      const taskWithConversations = await taskApi.getById(idToLoad);
      console.log('[Load Conversations] ✅ Task data received:', taskWithConversations);
      console.log('[Load Conversations] Total conversations:', taskWithConversations.conversations?.length || 0);
      console.log('[Load Conversations] Attachments in API response:', taskWithConversations.attachments?.length || 0);
      console.log('[Load Conversations] SubTasks in API response:', taskWithConversations.subTasks?.length || 0);
      
      if (taskWithConversations.conversations) {
        taskWithConversations.conversations.forEach((conv: any, idx: number) => {
          console.log(`[Load Conversations] Conversation ${idx + 1}:`, {
            id: conv.id,
            message: conv.message?.substring(0, 50) || '(empty)',
            hasAttachments: !!conv.attachments,
            attachmentsCount: conv.attachments?.length || 0,
            attachments: conv.attachments
          });
          
          if (conv.attachments && conv.attachments.length > 0) {
            conv.attachments.forEach((att: any, attIdx: number) => {
              console.log(`[Load Conversations]   Attachment ${attIdx + 1}:`, {
                id: att.id,
                fileName: att.fileName,
                filePath: att.filePath,
                fileSize: att.fileSize
              });
            });
          } else {
            console.log(`[Load Conversations]   ⚠️ No attachments for conversation ${conv.id}`);
          }
        });
      } else {
        console.warn('[Load Conversations] ⚠️ No conversations array in response');
      }
      
      // Preserve conversationsCount - use API value if available, otherwise calculate from conversations array, or keep original
      const originalTask = tasks.find(t => t.id === taskWithConversations.id);
      const conversationsCount = taskWithConversations.conversationsCount ?? 
                                 taskWithConversations.conversations?.length ?? 
                                 originalTask?.conversationsCount ?? 
                                 0;
      
      // Preserve subTasks and attachments from original task if not in API response
      // Check for both undefined/null and empty arrays
      const preservedSubTasks = (taskWithConversations.subTasks && taskWithConversations.subTasks.length > 0) 
        ? taskWithConversations.subTasks 
        : (originalTask?.subTasks && originalTask.subTasks.length > 0) 
          ? originalTask.subTasks 
          : (selectedTask?.subTasks && selectedTask.subTasks.length > 0) 
            ? selectedTask.subTasks 
            : originalTask?.subTasks ?? selectedTask?.subTasks;
      
      const preservedAttachments = (taskWithConversations.attachments && taskWithConversations.attachments.length > 0) 
        ? taskWithConversations.attachments 
        : (originalTask?.attachments && originalTask.attachments.length > 0) 
          ? originalTask.attachments 
          : (selectedTask?.attachments && selectedTask.attachments.length > 0) 
            ? selectedTask.attachments 
            : originalTask?.attachments ?? selectedTask?.attachments;
      
      console.log('[Load Conversations] Preserved attachments:', preservedAttachments?.length || 0);
      console.log('[Load Conversations] Original task attachments:', originalTask?.attachments?.length || 0);
      console.log('[Load Conversations] Selected task attachments:', selectedTask?.attachments?.length || 0);
      
      const updatedTask = {
        ...taskWithConversations,
        conversationsCount: conversationsCount,
        subTasks: preservedSubTasks,
        attachments: preservedAttachments
      };
      
      setSelectedTask(updatedTask);
      setTasks(prev => prev.map(t => {
        if (t.id === taskWithConversations.id) {
          // Always preserve conversationsCount, subTasks, and attachments
          const finalAttachments = preservedAttachments ?? t.attachments;
          const finalSubTasks = preservedSubTasks ?? t.subTasks;
          console.log('[Load Conversations] Final attachments for task in array:', finalAttachments?.length || 0);
          return {
            ...updatedTask,
            conversationsCount: conversationsCount,
            subTasks: finalSubTasks,
            attachments: finalAttachments
          };
        }
        return t;
      }));
      console.log('[Load Conversations] ✅ State updated');
    } catch (error) {
      console.error('[Load Conversations] ❌ Error loading conversations:', error);
    } finally {
      loadingConversationsRef.current = false;
      setLoadingConversations(false);
    }
  };

  const resetForm = () => {
    setEditingTask(null);
    setIsLimitedTask(false);
    setFormData({
      name: '',
      description: '',
      locationId: selectedLocationId ?? null,
      assignedToEmployeeIds: [],
      assignedToPositionIds: [],
      assignedToScheduleIds: [],
      dueDate: format(new Date(), 'yyyy-MM-dd'),
      status: 'Not Started',
      completedDate: '',
    });
    setAssignedToCategory('employees');
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (saving || isSelectedLocationInactive) return;

    if (!formData.name.trim()) {
      alert('Task name is required.');
      return;
    }
    if (!editingTask && !formData.locationId) {
      alert('Please select a location.');
      return;
    }
    const hasEmployees = (formData.assignedToEmployeeIds?.length || 0) > 0;
    const hasPositions = (formData.assignedToPositionIds?.length || 0) > 0;
    const hasSchedules = (formData.assignedToScheduleIds?.length || 0) > 0;
    
    if (!hasEmployees && !hasPositions && !hasSchedules) {
      alert('Please assign at least one employee, position, or schedule.');
      return;
    }
    
    // Validate completed date when status is Completed
    if (formData.status === 'Completed' && !formData.completedDate) {
      alert('Completed Date is required when status is Completed.');
      return;
    }

    try {
      setSaving(true);

      const formAssignments = {
        employeeIds: formData.assignedToEmployeeIds ?? [],
        positionIds: formData.assignedToPositionIds ?? [],
        scheduleIds: formData.assignedToScheduleIds ?? [],
      };
      const taskLoc = formData.locationId ?? selectedLocationId ?? null;
      let assignments = getNormalizedAssignments(
        formAssignments.employeeIds,
        formAssignments.positionIds,
        formAssignments.scheduleIds,
        taskLoc
      );
      const inactive = detectInactiveAssignees(
        formAssignments,
        assignments,
        buildAssignmentNameMaps(editingTask)
      );
      if (inactive.length > 0) {
        const choice = await promptInactiveAssigneesResolution(inactive);
        if (choice === 'cancel') return;
        if (choice === 'remove') {
          const stripped = stripInactiveAssignees(formAssignments, inactive);
          setFormData((prev) => ({
            ...prev,
            assignedToEmployeeIds: stripped.employeeIds,
            assignedToPositionIds: stripped.positionIds,
            assignedToScheduleIds: stripped.scheduleIds,
          }));
          assignments = getNormalizedAssignments(
            stripped.employeeIds,
            stripped.positionIds,
            stripped.scheduleIds,
            taskLoc
          );
        }
      }
      if (!hasAnyAssignmentIds(assignments)) {
        alert('No active assignees remain. Add at least one active employee, position, or schedule.');
        return;
      }
      
      // Create FormData for file uploads
      const formDataToSend = new FormData();
      formDataToSend.append('name', formData.name);
      if (formData.description) {
        formDataToSend.append('description', formData.description);
      }
      if (formData.locationId) {
        formDataToSend.append('locationId', formData.locationId.toString());
      }
      formDataToSend.append('dueDate', formData.dueDate);
      formDataToSend.append('status', formData.status || 'Not Started');
      if (formData.completedDate) {
        formDataToSend.append('completedDate', formData.completedDate);
      }
      
      assignments.employeeIds.forEach((id) => {
        formDataToSend.append('assignedToEmployeeIds', id.toString());
      });
      assignments.positionIds.forEach((id) => {
        formDataToSend.append('assignedToPositionIds', id.toString());
      });
      assignments.scheduleIds.forEach((id) => {
        formDataToSend.append('assignedToScheduleIds', id.toString());
      });
      
      // Add files
      console.log('[Task Submit] Adding files to FormData:', taskFormFiles.length);
      taskFormFiles.forEach((file, index) => {
        console.log(`[Task Submit] Adding file ${index + 1}:`, file.name, 'Size:', file.size);
        formDataToSend.append('files', file);
      });
      
      // Log FormData contents
      console.log('[Task Submit] FormData entries:');
      Array.from(formDataToSend.entries()).forEach(([key, value]) => {
        if (value instanceof File) {
          console.log(`  ${key}: File(${value.name}, ${value.size} bytes)`);
        } else {
          console.log(`  ${key}: ${value}`);
        }
      });
      
      // Delete attachments marked for deletion
      if (editingTask && attachmentsToDelete.length > 0) {
        for (const attachmentId of attachmentsToDelete) {
          try {
            await taskApi.deleteAttachment(editingTask.id, attachmentId);
          } catch (error) {
            console.error(`Error deleting attachment ${attachmentId}:`, error);
          }
        }
      }
      
      if (editingTask) {
        await taskApi.update(editingTask.id, formDataToSend);
      } else {
        await taskApi.create(formDataToSend);
      }

      const updated = selectedLocationId
        ? await taskApi.getAll({ locationId: selectedLocationId })
        : await taskApi.getAll();
      setTasks(updated);
      resetForm();
      setTaskFormFiles([]);
      setShowForm(false);
    } catch (error: any) {
      console.error('Error saving task:', error);
      const message = error?.response?.data?.message || error?.message || 'Failed to save task.';
      alert(message);
    } finally {
      setSaving(false);
    }
  };

  const startEdit = async (task: Task) => {
    if (isSelectedLocationInactive) return;
    if (myTasksTaskId !== null && task.id === myTasksTaskId && !canEditTask) return;
    setShowTaskView(false);
    setShowSubTaskView(false);
    setViewingSubTask(null);
    let entity: Task = task;
    try {
      entity = await taskApi.getById(task.id);
    } catch (error) {
      console.error('Error loading task for edit:', error);
    }
    const assignments = extractAssignmentFormIds(entity);
    setEditingTask(entity);
    setFormData({
      name: entity.name,
      description: entity.description || '',
      locationId: entity.locationId ?? selectedLocationId ?? null,
      assignedToEmployeeIds: assignments.assignedToEmployeeIds,
      assignedToPositionIds: assignments.assignedToPositionIds,
      assignedToScheduleIds: assignments.assignedToScheduleIds,
      dueDate: entity.dueDate ? format(new Date(entity.dueDate), 'yyyy-MM-dd') : format(new Date(), 'yyyy-MM-dd'),
      status: entity.status,
      completedDate: entity.completedAt ? format(new Date(entity.completedAt), 'yyyy-MM-dd') : '',
    });
    setAssignedToCategory('employees');
    setTaskFormFiles([]);
    setExistingTaskAttachments(task.attachments || []);
    setAttachmentsToDelete([]);
    setShowForm(true);
  };

  const handleDelete = async (id: number) => {
    if (isSelectedLocationInactive) return;
    if (!window.confirm('Are you sure you want to delete this task?')) return;
    try {
      await taskApi.delete(id);
      const updated = selectedLocationId
        ? await taskApi.getAll({ locationId: selectedLocationId })
        : await taskApi.getAll();
      setTasks(updated);
      if (selectedTask?.id === id) {
        setSelectedTask(null);
        setShowChat(false);
      }
    } catch (error: any) {
      console.error('Error deleting task:', error);
      const message = error?.response?.data?.message || error?.message || 'Failed to delete task.';
      alert(message);
    }
  };

  const loadSubTasks = async (taskId: number) => {
    try {
      const subTasksData = await subTaskApi.getAll(taskId);
      setSubTasks(prev => (subTasksData && subTasksData.length > 0 ? subTasksData : prev));
    } catch (error) {
      console.error('Error loading sub-tasks:', error);
    }
  };

  const resetSubTaskForm = () => {
    setEditingSubTask(null);
    setIsLimitedSubTask(false);
    setSubTaskFormData({
      name: '',
      description: '',
      assignedToEmployeeIds: [],
      assignedToPositionIds: [],
      assignedToScheduleIds: [],
      dueDate: format(new Date(), 'yyyy-MM-dd'),
      status: 'Not Started',
    });
    setSubTaskAssignedToCategory('employees');
  };

  const handleSubTaskSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedTask || saving || isSelectedLocationInactive) return;

    if (!subTaskFormData.name.trim()) {
      alert('Sub-task name is required.');
      return;
    }

    const hasEmployees = (subTaskFormData.assignedToEmployeeIds?.length || 0) > 0;
    const hasPositions = (subTaskFormData.assignedToPositionIds?.length || 0) > 0;
    const hasSchedules = (subTaskFormData.assignedToScheduleIds?.length || 0) > 0;
    
    if (!hasEmployees && !hasPositions && !hasSchedules) {
      alert('Please assign at least one employee, position, or schedule.');
      return;
    }
    
    // Validate completed date when status is Completed
    if (subTaskFormData.status === 'Completed' && !subTaskFormData.completedDate) {
      alert('Completed Date is required when status is Completed.');
      return;
    }

    try {
      setSaving(true);

      const formAssignments = {
        employeeIds: subTaskFormData.assignedToEmployeeIds ?? [],
        positionIds: subTaskFormData.assignedToPositionIds ?? [],
        scheduleIds: subTaskFormData.assignedToScheduleIds ?? [],
      };
      const taskLoc = selectedTask?.locationId ?? null;
      let assignments = getNormalizedAssignments(
        formAssignments.employeeIds,
        formAssignments.positionIds,
        formAssignments.scheduleIds,
        taskLoc
      );
      const inactive = detectInactiveAssignees(
        formAssignments,
        assignments,
        buildAssignmentNameMaps(editingSubTask)
      );
      if (inactive.length > 0) {
        const choice = await promptInactiveAssigneesResolution(inactive);
        if (choice === 'cancel') return;
        if (choice === 'remove') {
          const stripped = stripInactiveAssignees(formAssignments, inactive);
          setSubTaskFormData((prev) => ({
            ...prev,
            assignedToEmployeeIds: stripped.employeeIds,
            assignedToPositionIds: stripped.positionIds,
            assignedToScheduleIds: stripped.scheduleIds,
          }));
          assignments = getNormalizedAssignments(
            stripped.employeeIds,
            stripped.positionIds,
            stripped.scheduleIds,
            taskLoc
          );
        }
      }
      if (!hasAnyAssignmentIds(assignments)) {
        alert('No active assignees remain. Add at least one active employee, position, or schedule.');
        return;
      }
      
      // Create FormData for file uploads
      const formDataToSend = new FormData();
      formDataToSend.append('name', subTaskFormData.name);
      if (subTaskFormData.description) {
        formDataToSend.append('description', subTaskFormData.description);
      }
      formDataToSend.append('dueDate', subTaskFormData.dueDate);
      formDataToSend.append('status', subTaskFormData.status || 'Not Started');
      if (subTaskFormData.completedDate) {
        formDataToSend.append('completedDate', subTaskFormData.completedDate);
      }
      
      assignments.employeeIds.forEach((id) => {
        formDataToSend.append('assignedToEmployeeIds', id.toString());
      });
      assignments.positionIds.forEach((id) => {
        formDataToSend.append('assignedToPositionIds', id.toString());
      });
      assignments.scheduleIds.forEach((id) => {
        formDataToSend.append('assignedToScheduleIds', id.toString());
      });
      
      // Add files
      console.log('[SubTask Submit] Adding files to FormData:', subTaskFormFiles.length);
      subTaskFormFiles.forEach((file, index) => {
        console.log(`[SubTask Submit] Adding file ${index + 1}:`, file.name, 'Size:', file.size);
        formDataToSend.append('files', file);
      });
      
      // Log FormData contents
      console.log('[SubTask Submit] FormData entries:');
      Array.from(formDataToSend.entries()).forEach(([key, value]) => {
        if (value instanceof File) {
          console.log(`  ${key}: File(${value.name}, ${value.size} bytes)`);
        } else {
          console.log(`  ${key}: ${value}`);
        }
      });
      
      // Delete attachments marked for deletion
      if (editingSubTask && subTaskAttachmentsToDelete.length > 0) {
        for (const attachmentId of subTaskAttachmentsToDelete) {
          try {
            await subTaskApi.deleteAttachment(selectedTask.id, editingSubTask.id, attachmentId);
          } catch (error) {
            console.error(`Error deleting sub-task attachment ${attachmentId}:`, error);
          }
        }
      }
      
      if (editingSubTask) {
        await subTaskApi.update(selectedTask.id, editingSubTask.id, formDataToSend);
      } else {
        await subTaskApi.create(selectedTask.id, formDataToSend);
      }

      await loadSubTasks(selectedTask.id);
      resetSubTaskForm();
      setShowSubTaskForm(false);
    } catch (error: any) {
      console.error('Error saving sub-task:', error);
      const message = error?.response?.data?.message || error?.message || 'Failed to save sub-task.';
      alert(message);
    } finally {
      setSaving(false);
    }
  };

  const startEditSubTask = async (subTask: SubTask) => {
    if (myTasksTaskId !== null && viewingTask?.id === myTasksTaskId && !canEditSubTask) return;
    let entity: SubTask = subTask;
    if (selectedTask) {
      try {
        entity = await subTaskApi.getById(selectedTask.id, subTask.id);
      } catch (error) {
        console.error('Error loading sub-task for edit:', error);
      }
    }
    const assignments = extractAssignmentFormIds(entity);
    setEditingSubTask(entity);
    setSubTaskFormData({
      name: entity.name,
      description: entity.description || '',
      assignedToEmployeeIds: assignments.assignedToEmployeeIds,
      assignedToPositionIds: assignments.assignedToPositionIds,
      assignedToScheduleIds: assignments.assignedToScheduleIds,
      dueDate: entity.dueDate ? format(new Date(entity.dueDate), 'yyyy-MM-dd') : format(new Date(), 'yyyy-MM-dd'),
      status: entity.status,
      completedDate: entity.completedAt ? format(new Date(entity.completedAt), 'yyyy-MM-dd') : '',
    });
    setSubTaskAssignedToCategory('employees');
    setSubTaskFormFiles([]);
    setExistingSubTaskAttachments(subTask.attachments || []);
    setSubTaskAttachmentsToDelete([]);
    setShowSubTaskForm(true);
  };

  const handleDeleteSubTask = async (id: number) => {
    if (isSelectedLocationInactive) return;
    if (!selectedTask || !window.confirm('Are you sure you want to delete this sub-task?')) return;
    try {
      await subTaskApi.delete(selectedTask.id, id);
      await loadSubTasks(selectedTask.id);
      if (selectedSubTask?.id === id) {
        setSelectedSubTask(null);
        setShowSubTaskChat(false);
      }
    } catch (error: any) {
      console.error('Error deleting sub-task:', error);
      const message = error?.response?.data?.message || error?.message || 'Failed to delete sub-task.';
      alert(message);
    }
  };

  const openSubTaskChat = async (subTask: SubTask) => {
    if (!selectedTask) return;
    setSelectedSubTask(subTask);
    setShowSubTaskChat(true);
    await loadSubTaskConversations(selectedTask.id, subTask.id);
  };

  const loadSubTaskConversations = async (taskId: number, subTaskId: number) => {
    try {
      const conversations = await subTaskApi.getConversations(taskId, subTaskId);
      setSelectedSubTask(prev => prev ? { ...prev, conversations } : null);
    } catch (error) {
      console.error('Error loading sub-task conversations:', error);
    }
  };

  const handleSendSubTaskMessage = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedTask || !selectedSubTask || sendingSubTaskMessage || isSelectedLocationInactive) return;
    if (!newSubTaskMessage.trim() && selectedSubTaskFiles.length === 0) return;

    try {
      setSendingSubTaskMessage(true);
      const formData = new FormData();
      formData.append('message', newSubTaskMessage.trim() || '');
      if (replyingToSubTask?.id) {
        formData.append('replyToConversationId', replyingToSubTask.id.toString());
      }
      
      selectedSubTaskFiles.forEach((file) => {
        formData.append('files', file);
      });

      const response = await subTaskApi.addConversation(selectedTask.id, selectedSubTask.id, formData);
      
      setNewSubTaskMessage('');
      setSelectedSubTaskFiles([]);
      setReplyingToSubTask(null);
      
      await loadSubTaskConversations(selectedTask.id, selectedSubTask.id);
      
      // Scroll to bottom - use the scroll container
      setTimeout(() => {
        if (subTaskChatScrollContainerRef.current) {
          subTaskChatScrollContainerRef.current.scrollTop = subTaskChatScrollContainerRef.current.scrollHeight;
        } else if (subTaskChatEndRef.current) {
          subTaskChatEndRef.current.scrollIntoView({ behavior: 'smooth', block: 'end' });
        }
      }, 300);
    } catch (error: any) {
      console.error('Error sending sub-task message:', error);
      alert('Failed to send message. Please try again.');
    } finally {
      setSendingSubTaskMessage(false);
    }
  };

  const startRecordingSubTask = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mediaRecorder = new MediaRecorder(stream, {
        mimeType: 'audio/webm;codecs=opus'
      });
      
      mediaRecorderRefSubTask.current = mediaRecorder;
      audioChunksRefSubTask.current = [];
      
      mediaRecorder.ondataavailable = (event) => {
        if (event.data.size > 0) {
          audioChunksRefSubTask.current.push(event.data);
        }
      };
      
      mediaRecorder.onstop = () => {
        const audioBlob = new Blob(audioChunksRefSubTask.current, { type: 'audio/webm;codecs=opus' });
        const audioFile = new File([audioBlob], `voice-message-${Date.now()}.webm`, {
          type: 'audio/webm;codecs=opus'
        });
        
        setSelectedSubTaskFiles(prev => [...prev, audioFile]);
        
        // Stop all tracks to release microphone
        stream.getTracks().forEach(track => track.stop());
      };
      
      mediaRecorder.start();
      setIsRecordingSubTask(true);
      setRecordingTimeSubTask(0);
      
      // Start timer
      recordingTimerRefSubTask.current = setInterval(() => {
        setRecordingTimeSubTask(prev => prev + 1);
      }, 1000);
    } catch (error) {
      console.error('[Voice] Error starting recording:', error);
      alert('Failed to start recording. Please check microphone permissions.');
    }
  };

  const stopRecordingSubTask = () => {
    if (mediaRecorderRefSubTask.current && isRecordingSubTask) {
      mediaRecorderRefSubTask.current.stop();
      setIsRecordingSubTask(false);
      
      if (recordingTimerRefSubTask.current) {
        clearInterval(recordingTimerRefSubTask.current);
        recordingTimerRefSubTask.current = null;
      }
      
      setRecordingTimeSubTask(0);
    }
  };

  const handleSubTaskReaction = async (conversationId: number, reactionType: string) => {
    if (!selectedTask || !selectedSubTask) return;

    try {
      // Check if user already reacted with this type
      const conversation = selectedSubTask.conversations?.find(c => c.id === conversationId);
      const existingReaction = conversation?.reactions?.find(r => 
        r.type === reactionType && 
        r.employees.some(e => e.id === currentEmployee?.id)
      );

      let updatedReactions: TaskConversationReaction[];
      if (existingReaction) {
        // Remove reaction
        const response = await subTaskApi.removeReaction(selectedTask.id, selectedSubTask.id, conversationId);
        updatedReactions = response.reactions;
      } else {
        // Add or update reaction
        const response = await subTaskApi.addReaction(selectedTask.id, selectedSubTask.id, conversationId, reactionType);
        updatedReactions = response.reactions;
      }
      
      // Update state directly without reloading
      setSelectedSubTask(prev => {
        if (!prev) return prev;
        return {
          ...prev,
          conversations: prev.conversations?.map(conv => 
            conv.id === conversationId
              ? { ...conv, reactions: updatedReactions }
              : conv
          ) || []
        };
      });
      
      setShowSubTaskReactionPicker(null);
      setShowReactionDetails(null);
    } catch (error: any) {
      console.error('Error updating sub-task reaction:', error);
      alert('Failed to update reaction. Please try again.');
    }
  };

  const handleSubTaskFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files) {
      setSelectedSubTaskFiles(prev => [...prev, ...Array.from(e.target.files!)]);
    }
  };

  const removeSubTaskFile = (index: number) => {
    setSelectedSubTaskFiles(prev => prev.filter((_, i) => i !== index));
  };

  // Get assigned employees for mentions (task or sub-task)
  const getMentionableEmployees = (): Employee[] => {
    if (showSubTaskChat && selectedSubTask) {
      // Get employees assigned to sub-task
      const assignedEmployeeIds = selectedSubTask.assignedTo.map(e => e.id);
      return employees.filter(e => assignedEmployeeIds.includes(e.id));
    } else if (selectedTask) {
      // Get employees assigned to task
      const assignedEmployeeIds = selectedTask.assignedTo.map(e => e.id);
      return employees.filter(e => assignedEmployeeIds.includes(e.id));
    }
    return [];
  };

  // Handle @ mention in message input
  const handleMessageInputChange = (value: string, isSubTask: boolean = false) => {
    if (isSubTask) {
      setNewSubTaskMessage(value);
    } else {
      setNewMessage(value);
    }

    // Check for @ mention
    const cursorPos = isSubTask 
      ? subTaskMessageInputRef.current?.selectionStart || value.length
      : messageInputRef.current?.selectionStart || value.length;
    
    const textBeforeCursor = value.substring(0, cursorPos);
    const lastAtIndex = textBeforeCursor.lastIndexOf('@');
    
    if (lastAtIndex !== -1) {
      const textAfterAt = textBeforeCursor.substring(lastAtIndex + 1);
      // Check if there's no space after @ (meaning we're still in a mention)
      if (!textAfterAt.includes(' ') && !textAfterAt.includes('\n')) {
        setMentionQuery(textAfterAt.toLowerCase());
        setShowMentionList(true);
        
        // Calculate position for mention list
        const input = isSubTask ? subTaskMessageInputRef.current : messageInputRef.current;
        if (input) {
          const rect = input.getBoundingClientRect();
          setMentionPosition({
            top: rect.top - 200, // Position above input
            left: rect.left
          });
        }
        return;
      }
    }
    
    setShowMentionList(false);
    setMentionQuery('');
  };

  // Insert mention into message
  const insertMention = (employee: Employee, isSubTask: boolean = false) => {
    const input = isSubTask ? subTaskMessageInputRef.current : messageInputRef.current;
    if (!input) return;

    const currentValue = isSubTask ? newSubTaskMessage : newMessage;
    const cursorPos = input.selectionStart || currentValue.length;
    const textBeforeCursor = currentValue.substring(0, cursorPos);
    const textAfterCursor = currentValue.substring(cursorPos);
    
    const lastAtIndex = textBeforeCursor.lastIndexOf('@');
    if (lastAtIndex !== -1) {
      const beforeAt = currentValue.substring(0, lastAtIndex);
      const textAfterAt = textBeforeCursor.substring(lastAtIndex + 1);
      const mentionName = `@${employee.firstName} ${employee.lastName} `;
      const newText = `${beforeAt}${mentionName}${textAfterCursor}`;
      
      if (isSubTask) {
        setNewSubTaskMessage(newText);
      } else {
        setNewMessage(newText);
      }
      
      setShowMentionList(false);
      setMentionQuery('');
      
      // Set cursor position after the mention
      setTimeout(() => {
        const newCursorPos = lastAtIndex + mentionName.length;
        input.setSelectionRange(newCursorPos, newCursorPos);
        input.focus();
      }, 0);
    }
  };

  const openChat = async (task: Task) => {
    // Preserve the full task object including subTasks and attachments
    setSelectedTask(task);
    setShowChat(true);
    // Always load fresh conversations when opening chat
    // Pass task.id directly to avoid state timing issues
    await loadConversations(task.id);
  };

  const handleSendMessage = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedTask || sendingMessage || isSelectedLocationInactive) return;
    if (!newMessage.trim() && selectedFiles.length === 0) return;

    try {
      setSendingMessage(true);
      console.log('[Send Message] Starting send process');
      console.log('[Send Message] Task ID:', selectedTask.id);
      console.log('[Send Message] Message text:', newMessage.trim() || '(empty)');
      console.log('[Send Message] Selected files count:', selectedFiles.length);
      console.log('[Send Message] Selected files:', selectedFiles.map(f => ({ name: f.name, size: f.size, type: f.type })));
      console.log('[Send Message] Replying to:', replyingTo?.id || 'none');
      
      const formData = new FormData();
      formData.append('message', newMessage.trim() || '');
      if (replyingTo?.id) {
        formData.append('replyToConversationId', replyingTo.id.toString());
      }
      
      console.log('[Send Message] Adding files to FormData...');
      selectedFiles.forEach((file, index) => {
        console.log(`[Send Message] Adding file ${index + 1}:`, file.name, 'Size:', file.size, 'Type:', file.type);
        formData.append('files', file);
      });
      
      console.log('[Send Message] FormData entries:');
      const entries = Array.from(formData.entries());
      entries.forEach(([key, value]) => {
        if (value && typeof value === 'object' && 'name' in value && 'size' in value && 'type' in value) {
          const file = value as File;
          console.log(`  ${key}: File(${file.name}, ${file.size} bytes, ${file.type})`);
        } else {
          console.log(`  ${key}: ${value}`);
        }
      });

      console.log('[Send Message] Calling API...');
      const response = await taskApi.addConversation(selectedTask.id, formData);
      console.log('[Send Message] ✅ API Response received:', response);
      console.log('[Send Message] Response ID:', response.id);
      console.log('[Send Message] Response message:', response.message);
      console.log('[Send Message] Response attachments:', response.attachments);
      console.log('[Send Message] Response attachments count:', response.attachments?.length || 0);
      if (response.attachments && response.attachments.length > 0) {
        response.attachments.forEach((att: any, idx: number) => {
          console.log(`[Send Message] Attachment ${idx + 1}:`, {
            id: att.id,
            fileName: att.fileName,
            filePath: att.filePath,
            fileSize: att.fileSize,
            contentType: att.contentType
          });
        });
      } else {
        console.warn('[Send Message] ⚠️ No attachments in response!');
      }
      
      setNewMessage('');
      setReplyingTo(null);
      setSelectedFiles([]);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
      
      console.log('[Send Message] Reloading conversations...');
      // Reload conversations to ensure we have the latest data including attachments
      await loadConversations();
      
      // Scroll to bottom to show the new message - use the scroll container
      setTimeout(() => {
        if (chatScrollContainerRef.current) {
          chatScrollContainerRef.current.scrollTop = chatScrollContainerRef.current.scrollHeight;
        } else if (chatEndRef.current) {
          chatEndRef.current.scrollIntoView({ behavior: 'smooth', block: 'end' });
        }
      }, 300);
      
      console.log('[Send Message] ✅ Send process completed');
    } catch (error: any) {
      console.error('[Send Message] ❌ Error sending message:', error);
      console.error('[Send Message] Error details:', {
        message: error.message,
        response: error.response?.data,
        status: error.response?.status,
        statusText: error.response?.statusText,
        headers: error.response?.headers,
        config: error.config
      });
      
      // Show more detailed error message
      const errorMessage = error.response?.data?.message || error.response?.data?.error || error.message || 'Failed to send message. Please try again.';
      console.error('[Send Message] Error message to show:', errorMessage);
      console.error('[Send Message] Full error response:', JSON.stringify(error.response?.data, null, 2));
      alert(errorMessage);
    } finally {
      setSendingMessage(false);
    }
  };

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files) {
      const files = Array.from(e.target.files);
      console.log('[File Select] Files selected:', files.map(f => ({ name: f.name, size: f.size, type: f.type })));
      setSelectedFiles(prev => {
        const newFiles = [...prev, ...files];
        console.log('[File Select] Total selected files:', newFiles.length);
        return newFiles;
      });
    }
  };

  const removeFile = (index: number) => {
    setSelectedFiles(prev => prev.filter((_, i) => i !== index));
  };

  const startRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mediaRecorder = new MediaRecorder(stream, {
        mimeType: 'audio/webm;codecs=opus'
      });
      
      mediaRecorderRef.current = mediaRecorder;
      audioChunksRef.current = [];
      
      mediaRecorder.ondataavailable = (event) => {
        if (event.data.size > 0) {
          audioChunksRef.current.push(event.data);
        }
      };
      
      mediaRecorder.onstop = () => {
        const audioBlob = new Blob(audioChunksRef.current, { type: 'audio/webm;codecs=opus' });
        const audioFile = new File([audioBlob], `voice-message-${Date.now()}.webm`, {
          type: 'audio/webm;codecs=opus'
        });
        
        console.log('[Voice] Recording stopped. File created:', {
          name: audioFile.name,
          size: audioFile.size,
          type: audioFile.type
        });
        
        setSelectedFiles(prev => [...prev, audioFile]);
        
        // Stop all tracks to release microphone
        stream.getTracks().forEach(track => track.stop());
      };
      
      mediaRecorder.start();
      setIsRecording(true);
      setRecordingTime(0);
      
      // Start timer
      recordingTimerRef.current = setInterval(() => {
        setRecordingTime(prev => prev + 1);
      }, 1000);
      
      console.log('[Voice] Recording started');
    } catch (error) {
      console.error('[Voice] Error starting recording:', error);
      alert('Failed to start recording. Please check microphone permissions.');
    }
  };

  const stopRecording = () => {
    if (mediaRecorderRef.current && isRecording) {
      mediaRecorderRef.current.stop();
      setIsRecording(false);
      
      if (recordingTimerRef.current) {
        clearInterval(recordingTimerRef.current);
        recordingTimerRef.current = null;
      }
      
      setRecordingTime(0);
      console.log('[Voice] Recording stopped');
    }
  };

  const formatRecordingTime = (seconds: number) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const formatFileSize = (bytes: number) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return Math.round(bytes / Math.pow(k, i) * 100) / 100 + ' ' + sizes[i];
  };

  const handleReaction = async (conversationId: number, reactionType: string) => {
    if (!selectedTask) return;

    try {
      // Check if user already reacted with this type
      const conversation = selectedTask.conversations?.find(c => c.id === conversationId);
      const existingReaction = conversation?.reactions?.find(r => 
        r.type === reactionType && 
        r.employees.some(e => e.id === currentEmployee?.id)
      );

      let updatedReactions: TaskConversationReaction[];
      if (existingReaction) {
        // Remove reaction
        const response = await taskApi.removeReaction(selectedTask.id, conversationId);
        updatedReactions = response.reactions;
      } else {
        // Add or update reaction
        const response = await taskApi.addReaction(selectedTask.id, conversationId, reactionType);
        updatedReactions = response.reactions;
      }
      
      // Update state directly without reloading
      setSelectedTask(prev => {
        if (!prev) return prev;
        return {
          ...prev,
          conversations: prev.conversations?.map(conv => 
            conv.id === conversationId
              ? { ...conv, reactions: updatedReactions }
              : conv
          ) || []
        };
      });
      
      setShowReactionPicker(null);
      setShowReactionDetails(null);
    } catch (error: any) {
      console.error('Error updating reaction:', error);
      alert('Failed to update reaction. Please try again.');
    }
  };

  const getReactionEmoji = (type: string) => {
    const emojis: Record<string, string> = {
      like: '👍',
      love: '❤️',
      laugh: '😂',
      wow: '😮',
      sad: '😢',
      angry: '😠'
    };
    return emojis[type] || '👍';
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'Completed':
        return 'bg-green-100 text-green-800 border-green-200';
      case 'InProgress':
        return 'bg-blue-100 text-blue-800 border-blue-200';
      case 'Not Started':
        return 'bg-gray-100 text-gray-800 border-gray-200';
      default:
        return 'bg-gray-100 text-gray-800 border-gray-200';
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'Completed':
        return <CheckCircle className="h-4 w-4" />;
      case 'InProgress':
        return <Clock className="h-4 w-4" />;
      case 'Not Started':
        return <AlertCircle className="h-4 w-4" />;
      default:
        return <AlertCircle className="h-4 w-4" />;
    }
  };

  const isOverdue = (dueDate: string, status: string) => {
    if (status === 'Completed') return false;
    return new Date(dueDate) < new Date();
  };

  // Normalize attachment URL - sub-task conversation attachments were previously saved with full filesystem path
  const getAttachmentUrl = (filePath: string | undefined, uploadFolder: 'task-conversations' | 'subtask-conversations' = 'task-conversations'): string => {
    if (!filePath?.trim()) return '';
    let path = filePath.trim();
    // API may return presigned S3 URLs or absolute URLs — do not prefix with API host
    if (/^https?:\/\//i.test(path)) return path;
    const base = getApiBaseUrlNoPath();
    if (path.includes('\\') || /^[A-Za-z]:/.test(path)) {
      const fileName = path.split(/[/\\]/).pop() || '';
      path = `/uploads/${uploadFolder}/${fileName}`;
    }
    return `${base}${path.startsWith('/') ? '' : '/'}${path}`;
  };

  // Circle chart: time passed vs remaining (createdAt -> dueDate)
  const getTimeProgress = (task: Task) => {
    const start = new Date(task.createdAt).getTime();
    const end = new Date(task.dueDate).getTime();
    const now = Date.now();
    if (end <= start) return 100; // Edge case: invalid range
    if (task.status === 'Completed') return 100;
    const total = end - start;
    const elapsed = Math.min(Math.max(now - start, 0), total);
    return Math.round((elapsed / total) * 100);
  };

  // Circle chart: subtasks completed vs remaining
  const getSubTaskProgress = (task: Task) => {
    const subs = task.subTasks || [];
    if (subs.length === 0) return { completed: 0, total: 0, percent: 0 };
    const completed = subs.filter((s) => s.status === 'Completed').length;
    return { completed, total: subs.length, percent: Math.round((completed / subs.length) * 100) };
  };

  const ProgressCircle: React.FC<{ percent: number; color: string; size?: number; title: string; label: string }> = ({ percent, color, size = 36, title, label }) => {
    const r = (size / 2) - 4;
    const circ = 2 * Math.PI * r;
    const filled = (percent / 100) * circ;
    return (
      <div className="relative flex flex-col items-center" title={title}>
        <svg width={size} height={size} className="flex-shrink-0">
          <circle
            cx={size / 2}
            cy={size / 2}
            r={r}
            fill="none"
            stroke="currentColor"
            strokeWidth="4"
            className="text-neutral-200"
          />
          <circle
            cx={size / 2}
            cy={size / 2}
            r={r}
            fill="none"
            stroke={color}
            strokeWidth="4"
            strokeDasharray={`${filled} ${circ}`}
            strokeLinecap="round"
            transform={`rotate(-90 ${size / 2} ${size / 2})`}
          />
        </svg>
        <span className="text-[10px] font-medium text-neutral-600 mt-0.5">{label}</span>
      </div>
    );
  };

  if (permissionsLoading) {
    return (
      <div className="flex items-center justify-center min-h-[200px]">
        <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-blue-600" />
      </div>
    );
  }

  if (loading) {
    return (
      <div className="p-6">
        <div className="flex items-center justify-center h-64">
          <div className="text-neutral-500">Loading tasks...</div>
        </div>
      </div>
    );
  }

  // Managers need a location to load the full task list; assignees opening from My Tasks load one task without it
  if (!selectedLocationId && hasTaskManagement) {
    return (
      <div className="flex h-full items-center justify-center bg-gray-50">
        <div className="flex flex-col items-center text-gray-500 max-w-md text-center px-4">
          <div className="flex items-center justify-center w-16 h-16 rounded-full bg-blue-50 mb-4">
            <MapPin className="w-8 h-8 text-blue-500" />
          </div>
          <h2 className="text-xl font-semibold text-gray-900 mb-2">
            Select a Location
          </h2>
          <p className="text-sm">
            Please select a specific location from the dropdown in the top navigation bar to access task admin features.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6">
      <div className="flex justify-between items-center">
        <h1 className="text-2xl font-bold text-neutral-800">
          {hasTaskManagement ? 'Task Admin' : 'Task'}
        </h1>
        {hasTaskManagement && !isSelectedLocationInactive && (
        <button
          onClick={() => {
            resetForm();
            setShowForm(true);
          }}
          className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
        >
          <Plus className="h-4 w-4" />
          Add Task
        </button>
        )}
      </div>

      {isSelectedLocationInactive && selectedLocation && (
        <InactiveLocationBanner locationName={selectedLocation.name} />
      )}

      {/* Task List */}
      <div className="bg-white rounded-lg shadow overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-neutral-50 border-b border-neutral-200">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-neutral-500 uppercase tracking-wider">Name</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-neutral-500 uppercase tracking-wider">Created By</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-neutral-500 uppercase tracking-wider">Assigned To</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-neutral-500 uppercase tracking-wider">Due Date</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-neutral-500 uppercase tracking-wider">Status</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-neutral-500 uppercase tracking-wider">Progress</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-neutral-500 uppercase tracking-wider">Actions</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-neutral-200">
              {tasks.length === 0 ? (
                <tr>
                  <td colSpan={7} className="px-6 py-8 text-center text-neutral-500">
                    No tasks found. Create your first task to get started.
                  </td>
                </tr>
              ) : (
                tasks.map((task) => (
                  <tr key={task.id} className="hover:bg-neutral-50">
                    <td className="px-6 py-4">
                      <div className="text-sm font-medium text-neutral-900">{task.name}</div>
                      {task.description && (
                        <div className="text-sm text-neutral-500 mt-1 line-clamp-2">{task.description}</div>
                      )}
                      {task.attachments && task.attachments.length > 0 && (
                        <div className="mt-2 space-y-1">
                          <div className="text-xs font-medium text-neutral-500">Attachments:</div>
                          {task.attachments.map((attachment) => (
                            <a
                              key={attachment.id}
                              href={getUploadedFileUrl(attachment.filePath)}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="flex items-center gap-1 text-xs text-blue-600 hover:text-blue-800 hover:underline"
                              title={attachment.fileName}
                            >
                              <FileIcon className="h-3 w-3 flex-shrink-0" />
                              <span className="truncate max-w-[200px]">{attachment.fileName}</span>
                              <span className="text-neutral-500">({formatFileSize(attachment.fileSize)})</span>
                            </a>
                          ))}
                        </div>
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-neutral-900">
                        {task.createdBy?.firstName} {task.createdBy?.lastName}
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-2 flex-wrap">
                        {/* Employee Circles */}
                        {task.assignedTo && task.assignedTo.length > 0 && (
                          <>
                            {task.assignedTo.map((employee) => {
                              const initials = `${employee.firstName?.[0] || ''}${employee.lastName?.[0] || ''}`.toUpperCase();
                              const fullName = `${employee.firstName} ${employee.lastName}`;
                              const empProfileUrl = getProfileImageUrl((employee as { profileImageUrl?: string }).profileImageUrl ?? employees.find(e => e.id === employee.id)?.profileImageUrl, (employee as { updatedAt?: string }).updatedAt ?? employees.find(e => e.id === employee.id)?.updatedAt);
                              return (
                                <div
                                  key={employee.id}
                                  className="relative group"
                                >
                                  {empProfileUrl ? (
                                    <img src={empProfileUrl} alt={fullName} className="w-8 h-8 rounded-full object-cover cursor-pointer hover:ring-2 hover:ring-blue-600 transition-colors" />
                                  ) : (
                                    <div className="w-8 h-8 rounded-full bg-blue-500 text-white flex items-center justify-center text-xs font-semibold cursor-pointer hover:bg-blue-600 transition-colors">
                                      {initials}
                                    </div>
                                  )}
                                  <div className="absolute left-0 bottom-full mb-2 hidden group-hover:block z-10">
                                    <div className="bg-neutral-800 text-white text-xs rounded py-1 px-2 whitespace-nowrap">
                                      {fullName}
                                    </div>
                                    <div className="w-0 h-0 border-l-4 border-r-4 border-t-4 border-transparent border-t-neutral-800 ml-2"></div>
                                  </div>
                                </div>
                              );
                            })}
                          </>
                        )}
                        {/* Position — link opens employee list popup */}
                        {(task.assignedToPositions && task.assignedToPositions.length > 0
                          ? task.assignedToPositions
                          : (task.assignedToPositionIds || []).map(positionId => ({ id: positionId, title: positions.find(p => p.id === positionId)?.title ?? '' })).filter(p => p.title)
                        ).map((position) => (
                          <AssignmentCircleLink
                            key={`pos-${position.id}`}
                            kind="position"
                            label={position.title}
                            size="md"
                            onClick={() => openAssignmentEmployeesModal('position', position.id, position.title, task.locationId)}
                          />
                        ))}
                        {/* Schedule — circle + underlined initials opens employee list */}
                        {((task.assignedToSchedules && task.assignedToSchedules.length > 0)
                          ? task.assignedToSchedules
                          : (task.assignedToScheduleIds || []).map(scheduleId => ({ id: scheduleId, name: schedules.find(s => s.id === scheduleId)?.name ?? '' })).filter(s => s.name)
                        ).map((schedule) => (
                          <AssignmentCircleLink
                            key={`sched-${schedule.id}`}
                            kind="schedule"
                            label={schedule.name}
                            size="md"
                            onClick={() => openAssignmentEmployeesModal('schedule', schedule.id, schedule.name, task.locationId)}
                          />
                        ))}
                        {(!task.assignedTo || task.assignedTo.length === 0) &&
                         (task.assignedToPositions?.length ?? task.assignedToPositionIds?.length ?? 0) === 0 &&
                         (task.assignedToSchedules?.length ?? task.assignedToScheduleIds?.length ?? 0) === 0 && (
                          <span className="text-xs text-neutral-400">Unassigned</span>
                        )}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center gap-2">
                        <Calendar className="h-4 w-4 text-neutral-400" />
                        <span className={`text-sm ${isOverdue(task.dueDate, task.status) ? 'text-red-600 font-semibold' : 'text-neutral-900'}`}>
                          {formatCompanyDate(task.dueDate)}
                        </span>
                        {isOverdue(task.dueDate, task.status) && (
                          <span className="text-xs text-red-600">(Overdue)</span>
                        )}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex flex-col gap-1">
                      <span className={`inline-flex items-center gap-1 px-3 py-1 rounded-full text-xs font-semibold border ${getStatusColor(task.status)}`}>
                        {getStatusIcon(task.status)}
                        {task.status}
                      </span>
                        {task.status === 'Completed' && task.completedAt && (
                          <span className="text-xs text-neutral-500">
                            Completed: {formatCompanyDate(task.completedAt)}
                          </span>
                        )}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center gap-4">
                        <ProgressCircle
                          percent={getTimeProgress(task)}
                          color="#3b82f6"
                          title={`Time: ${getTimeProgress(task)}% passed, ${100 - getTimeProgress(task)}% remaining`}
                          label={`${getTimeProgress(task)}% passed`}
                        />
                        {getSubTaskProgress(task).total > 0 ? (
                          <ProgressCircle
                            percent={getSubTaskProgress(task).percent}
                            color="#22c55e"
                            title={`Sub-tasks: ${getSubTaskProgress(task).completed} completed, ${getSubTaskProgress(task).total - getSubTaskProgress(task).completed} remaining`}
                            label={`${getSubTaskProgress(task).completed}/${getSubTaskProgress(task).total}`}
                          />
                        ) : (
                          <div className="flex flex-col items-center" title="No sub-tasks">
                            <div className="w-9 h-9 rounded-full border-2 border-dashed border-neutral-300 flex items-center justify-center text-neutral-400 text-xs">0</div>
                            <span className="text-[10px] font-medium text-neutral-500 mt-0.5">N/A</span>
                          </div>
                        )}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                      <div className="flex items-center gap-2">
                        <button
                          onClick={() => {
                            setSelectedTask(task);
                            setShowChat(false);
                            setShowSubTaskChat(false);
                            if (task.subTasks && task.subTasks.length > 0) {
                              setSubTasks(task.subTasks as SubTask[]);
                            } else {
                              setSubTasks([]);
                            }
                          }}
                          className="text-green-600 hover:text-green-900 p-1 rounded hover:bg-green-50 relative"
                          title="View Sub-Tasks"
                        >
                          <GitBranch className="h-4 w-4" />
                          {task.subTasks && task.subTasks.length > 0 && (
                            <span className="absolute -top-2 -right-2 bg-green-600 text-white text-xs rounded-full h-5 w-5 flex items-center justify-center border-2 border-white">
                              {task.subTasks.length}
                            </span>
                          )}
                        </button>
                        <button
                          onClick={() => openChat(task)}
                          className="text-blue-600 hover:text-blue-900 p-1 rounded hover:bg-blue-50 relative"
                          title="View Conversations"
                        >
                          <MessageSquare className="h-4 w-4" />
                          {task.conversationsCount !== undefined && task.conversationsCount > 0 && (
                            <span className="absolute -top-2 -right-2 bg-blue-600 text-white text-xs rounded-full h-5 w-5 flex items-center justify-center border-2 border-white">
                              {task.conversationsCount}
                            </span>
                          )}
                        </button>
                        <button
                          onClick={async () => {
                            setViewingTask(task);
                            setShowTaskView(true);
                            // Load full subtask details
                            if (task.id) {
                              try {
                                const subTasksData = await subTaskApi.getAll(task.id);
                                setViewingSubTasks(subTasksData);
                              } catch (error) {
                                console.error('Error loading subtasks for view:', error);
                                setViewingSubTasks([]);
                              }
                            } else {
                              setViewingSubTasks([]);
                            }
                          }}
                          className="text-neutral-600 hover:text-neutral-900 p-1 rounded hover:bg-neutral-100"
                          title="View Task Details"
                        >
                          <Eye className="h-4 w-4" />
                        </button>
                        {!(myTasksTaskId !== null && task.id === myTasksTaskId && !canEditTask) && !isSelectedLocationInactive && (
                        <button
                          onClick={() => startEdit(task)}
                          className="text-neutral-600 hover:text-neutral-900 p-1 rounded hover:bg-neutral-100"
                          title="Edit Task"
                        >
                          <Edit className="h-4 w-4" />
                        </button>
                        )}
                        {hasSuperAdminRole && !isSelectedLocationInactive && (
                        <button
                          onClick={() => handleDelete(task.id)}
                          className="text-red-600 hover:text-red-900 p-1 rounded hover:bg-red-50"
                          title="Delete Task (Super Admin only)"
                        >
                          <Trash2 className="h-4 w-4" />
                        </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Create/Edit Form Modal */}
      {showForm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <div className="flex justify-between items-center mb-6">
                <h2 className="text-xl font-bold text-neutral-800">
                  {editingTask ? 'Edit Task' : 'Create New Task'}
                </h2>
                <button
                  onClick={() => {
                    setShowForm(false);
                    resetForm();
                  }}
                  className="text-neutral-400 hover:text-neutral-600"
                >
                  <X className="h-5 w-5" />
                </button>
              </div>

              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-neutral-700 mb-1">
                    Task Name <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    className="w-full px-3 py-2 border border-neutral-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-neutral-700 mb-1">
                    Description
                  </label>
                  <textarea
                    value={formData.description}
                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                    className="w-full px-3 py-2 border border-neutral-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    rows={4}
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-neutral-700 mb-1">
                    Location <span className="text-red-500">*</span>
                  </label>
                  <select
                    value={formData.locationId ?? ''}
                    onChange={(e) => setFormData({ ...formData, locationId: e.target.value ? Number(e.target.value) : null })}
                    disabled={isLimitedTask}
                    className="w-full px-3 py-2 border border-neutral-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-50"
                    required
                  >
                    <option value="">Select Location</option>
                    {locations.map((loc) => (
                      <option key={loc.id} value={loc.id}>{loc.name}</option>
                    ))}
                  </select>
                </div>

                {isLimitedTask && editingTask && (
                  <div className="px-3 py-2 rounded-lg bg-amber-50 border border-amber-200 text-amber-800 text-sm">
                    Limited access: You cannot change assignments, due date, or status.
                  </div>
                )}

                <div>
                  <label className="block text-sm font-medium text-neutral-700 mb-2">
                    Assigned To <span className="text-red-500">*</span>
                  </label>
                  
                  {/* Category Tabs */}
                  <div className={`flex gap-2 mb-3 border-b border-neutral-200 ${isLimitedTask ? 'opacity-60 pointer-events-none' : ''}`}>
                    <button
                      type="button"
                      onClick={() => setAssignedToCategory('employees')}
                      className={`px-4 py-2 text-sm font-medium transition-colors ${
                        assignedToCategory === 'employees'
                          ? 'text-blue-600 border-b-2 border-blue-600'
                          : 'text-neutral-600 hover:text-neutral-900'
                      }`}
                    >
                      Employees ({formData.assignedToEmployeeIds.length})
                    </button>
                    <button
                      type="button"
                      onClick={() => setAssignedToCategory('positions')}
                      className={`px-4 py-2 text-sm font-medium transition-colors ${
                        assignedToCategory === 'positions'
                          ? 'text-blue-600 border-b-2 border-blue-600'
                          : 'text-neutral-600 hover:text-neutral-900'
                      }`}
                    >
                      Positions ({(formData.assignedToPositionIds || []).length})
                    </button>
                    <button
                      type="button"
                      onClick={() => setAssignedToCategory('schedules')}
                      className={`px-4 py-2 text-sm font-medium transition-colors ${
                        assignedToCategory === 'schedules'
                          ? 'text-blue-600 border-b-2 border-blue-600'
                          : 'text-neutral-600 hover:text-neutral-900'
                      }`}
                    >
                      Schedules ({(formData.assignedToScheduleIds || []).length})
                    </button>
                  </div>

                  {/* Category Content */}
                  {assignedToCategory === 'employees' && (
                    <div className={isLimitedTask ? 'opacity-60 pointer-events-none' : ''}>
                      <BasketSelect
                        label=""
                        availableItems={employeesFilteredByLocation.map(emp => ({
                          id: emp.id,
                          label: `${emp.firstName} ${emp.lastName} (${emp.email})`
                        }))}
                        extraSelectedItems={employeeBasketExtras(editingTask ?? {})}
                        selectedIds={formData.assignedToEmployeeIds}
                        onChange={(ids) => setFormData({ ...formData, assignedToEmployeeIds: ids as number[] })}
                        placeholder="Select employees..."
                      />
                    </div>
                  )}

                  {assignedToCategory === 'positions' && (
                    <div className={isLimitedTask ? 'opacity-60 pointer-events-none' : ''}>
                      <BasketSelect
                        label=""
                        availableItems={positions.map(pos => ({
                          id: pos.id,
                          label: pos.title
                        }))}
                        extraSelectedItems={positionBasketExtras(editingTask ?? {})}
                        selectedIds={formData.assignedToPositionIds || []}
                        onChange={(ids) => setFormData({ ...formData, assignedToPositionIds: ids as number[] })}
                        placeholder="Select positions..."
                      />
                    </div>
                  )}

                  {assignedToCategory === 'schedules' && (
                    <div className={isLimitedTask ? 'opacity-60 pointer-events-none' : ''}>
                      <BasketSelect
                        label=""
                        availableItems={schedulesFilteredByLocation.map(schedule => ({
                          id: schedule.id,
                          label: schedule.name
                        }))}
                        extraSelectedItems={scheduleBasketExtras(editingTask ?? {})}
                        selectedIds={formData.assignedToScheduleIds || []}
                        onChange={(ids) => setFormData({ ...formData, assignedToScheduleIds: ids as number[] })}
                        placeholder="Select schedules..."
                      />
                    </div>
                  )}
                </div>

                <div className={isLimitedTask ? 'opacity-60 pointer-events-none' : ''}>
                  <label className="block text-sm font-medium text-neutral-700 mb-1">
                    Due Date <span className="text-red-500">*</span>
                  </label>
                        <input
                    type="date"
                    value={formData.dueDate}
                    onChange={(e) => setFormData({ ...formData, dueDate: e.target.value })}
                    className="w-full px-3 py-2 border border-neutral-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    required
                  />
                </div>

                <div className={isLimitedTask ? 'opacity-60 pointer-events-none' : ''}>
                  <label className="block text-sm font-medium text-neutral-700 mb-1">
                    Status
                  </label>
                  <select
                    value={formData.status}
                          onChange={(e) => {
                      const newStatus = e.target.value as any;
                              setFormData({
                                ...formData,
                        status: newStatus,
                        completedDate: newStatus === 'Completed' && !formData.completedDate ? format(new Date(), 'yyyy-MM-dd') : formData.completedDate
                      });
                    }}
                    className="w-full px-3 py-2 border border-neutral-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="Not Started">Not Started</option>
                    <option value="InProgress">InProgress</option>
                    <option value="Completed">Completed</option>
                  </select>
                </div>

                {formData.status === 'Completed' && (
                  <div>
                    <label className="block text-sm font-medium text-neutral-700 mb-1">
                      Completed Date <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="date"
                      value={formData.completedDate || format(new Date(), 'yyyy-MM-dd')}
                      onChange={(e) => setFormData({ ...formData, completedDate: e.target.value })}
                      className="w-full px-3 py-2 border border-neutral-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      required
                    />
                  </div>
                )}

                <div>
                  <label className="block text-sm font-medium text-neutral-700 mb-1">
                    Attachments
                  </label>
                  <input
                    type="file"
                    multiple
                    onChange={(e) => {
                      if (e.target.files && e.target.files.length > 0) {
                        const newFiles = Array.from(e.target.files);
                        console.log('[Task Form] Files selected:', newFiles.map(f => ({ name: f.name, size: f.size, type: f.type })));
                        setTaskFormFiles([...taskFormFiles, ...newFiles]);
                      }
                    }}
                    className="w-full px-3 py-2 border border-neutral-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                  
                  {/* Existing Attachments */}
                  {existingTaskAttachments.length > 0 && (
                    <div className="mt-2 space-y-1">
                      <div className="text-xs font-medium text-neutral-500">Existing Attachments:</div>
                      {existingTaskAttachments.map((attachment) => (
                        <div key={attachment.id} className="flex items-center gap-2 px-2 py-1 bg-neutral-50 border border-neutral-200 rounded text-sm">
                          <FileIcon className="h-4 w-4 text-neutral-600" />
                          <span className="text-neutral-700 flex-1 truncate">{attachment.fileName}</span>
                          <span className="text-xs text-neutral-500">({formatFileSize(attachment.fileSize)})</span>
                          <button
                            type="button"
                            onClick={() => {
                              setAttachmentsToDelete([...attachmentsToDelete, attachment.id]);
                              setExistingTaskAttachments(existingTaskAttachments.filter(a => a.id !== attachment.id));
                            }}
                            className="text-red-600 hover:text-red-800"
                            title="Remove attachment"
                          >
                            <X className="h-4 w-4" />
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                  
                  {/* New Files */}
                  {taskFormFiles.length > 0 && (
                    <div className="mt-2 space-y-1">
                      <div className="text-xs font-medium text-neutral-500">New Files:</div>
                      <div className="flex flex-wrap gap-2">
                        {taskFormFiles.map((file, index) => (
                          <div key={index} className="flex items-center gap-2 px-2 py-1 bg-blue-50 border border-blue-200 rounded text-sm">
                            <FileIcon className="h-4 w-4 text-blue-600" />
                            <span className="text-neutral-700">{file.name}</span>
                            <button
                              type="button"
                              onClick={() => setTaskFormFiles(taskFormFiles.filter((_, i) => i !== index))}
                              className="text-red-600 hover:text-red-800"
                            >
                              <X className="h-4 w-4" />
                            </button>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>

                <div className="flex justify-end gap-3 pt-4">
                  <button
                    type="button"
                    onClick={() => {
                      setShowForm(false);
                      resetForm();
                    }}
                    className="px-4 py-2 border border-neutral-300 rounded-md text-neutral-700 hover:bg-neutral-50"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={saving}
                    className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50"
                  >
                    {saving ? 'Saving...' : editingTask ? 'Update Task' : 'Create Task'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* Task View Modal */}
      {showTaskView && viewingTask && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-3xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <div className="flex justify-between items-center mb-6">
                <h2 className="text-2xl font-bold text-neutral-800">Task Details</h2>
                <div className="flex items-center gap-2">
                  {canEditTask && (
                    <button
                      onClick={() => {
                        setShowTaskView(false);
                        startEdit(viewingTask!);
                      }}
                      className="flex items-center gap-1 px-3 py-2 text-sm font-medium rounded-lg bg-blue-600 text-white hover:bg-blue-700"
                    >
                      <Edit className="h-4 w-4" />
                      Edit Task
                    </button>
                  )}
                  <button
                    onClick={() => {
                      setShowTaskView(false);
                      setViewingTask(null);
                      setViewingSubTasks([]);
                    }}
                    className="text-neutral-400 hover:text-neutral-600"
                  >
                    <X className="h-5 w-5" />
                  </button>
                </div>
              </div>

              <div className="space-y-6">
                {/* Task Name */}
                <div>
                  <h3 className="text-lg font-semibold text-neutral-800 mb-2">{viewingTask.name}</h3>
                  {viewingTask.description && (
                    <p className="text-neutral-600 whitespace-pre-wrap">{viewingTask.description}</p>
                  )}
                </div>

                {/* Task Information Grid */}
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="text-sm font-medium text-neutral-500">Created By</label>
                    <p className="text-neutral-900 mt-1">
                      {viewingTask.createdBy?.firstName} {viewingTask.createdBy?.lastName}
                    </p>
                  </div>
                  <div>
                    <label className="text-sm font-medium text-neutral-500">Status</label>
                    <p className="text-neutral-900 mt-1">
                      <span className={`inline-flex items-center gap-1 px-3 py-1 rounded-full text-xs font-semibold border ${getStatusColor(viewingTask.status)}`}>
                        {getStatusIcon(viewingTask.status)}
                        {viewingTask.status}
                        </span>
                    </p>
                  </div>
                  <div>
                    <label className="text-sm font-medium text-neutral-500">Due Date</label>
                    <p className="text-neutral-900 mt-1 flex items-center gap-2">
                      <Calendar className="h-4 w-4 text-neutral-400" />
                      {formatCompanyDate(viewingTask.dueDate)}
                    </p>
                  </div>
                  {viewingTask.status === 'Completed' && viewingTask.completedAt && (
                    <div>
                      <label className="text-sm font-medium text-neutral-500">Completed Date</label>
                      <p className="text-neutral-900 mt-1 flex items-center gap-2">
                        <CheckCircle className="h-4 w-4 text-neutral-400" />
                        {formatCompanyDate(viewingTask.completedAt)}
                      </p>
                    </div>
                  )}
                  <div>
                    <label className="text-sm font-medium text-neutral-500">Created At</label>
                    <p className="text-neutral-900 mt-1">
                      {formatCompanyDateTime(viewingTask.createdAt)}
                    </p>
                  </div>
                  {viewingTask.updatedAt && (
                    <div>
                      <label className="text-sm font-medium text-neutral-500">Last Updated</label>
                      <p className="text-neutral-900 mt-1">
                        {formatCompanyDateTime(viewingTask.updatedAt)}
                      </p>
                    </div>
                  )}
                </div>

                {/* Assigned To */}
                <div>
                  <label className="text-sm font-medium text-neutral-500 mb-3 block">Assigned To</label>
                  <div className="space-y-3">
                    {/* Employees */}
                    {viewingTask.assignedTo && viewingTask.assignedTo.length > 0 && (
                      <div>
                        <h4 className="text-sm font-semibold text-neutral-700 mb-2">Employees</h4>
                        <div className="flex flex-wrap gap-2">
                          {viewingTask.assignedTo.map((employee) => {
                            const empProfileUrl = getProfileImageUrl((employee as { profileImageUrl?: string }).profileImageUrl ?? employees.find(e => e.id === employee.id)?.profileImageUrl, (employee as { updatedAt?: string }).updatedAt ?? employees.find(e => e.id === employee.id)?.updatedAt);
                            return (
                            <div key={employee.id} className="flex items-center gap-2 px-3 py-2 bg-blue-50 rounded-lg">
                              {empProfileUrl ? (
                                <img src={empProfileUrl} alt={`${employee.firstName} ${employee.lastName}`} className="w-8 h-8 rounded-full object-cover" />
                              ) : (
                                <div className="w-8 h-8 rounded-full bg-blue-500 text-white flex items-center justify-center text-xs font-semibold">
                                  {`${employee.firstName?.[0] || ''}${employee.lastName?.[0] || ''}`.toUpperCase()}
                                </div>
                              )}
                              <span className="text-sm text-neutral-900">
                                {employee.firstName} {employee.lastName}
                              </span>
                            </div>
                          );})}
                        </div>
                      </div>
                    )}
                    {/* Positions */}
                    {((viewingTask.assignedToPositions && viewingTask.assignedToPositions.length > 0) || (viewingTask.assignedToPositionIds && viewingTask.assignedToPositionIds.length > 0)) && (
                      <div>
                        <h4 className="text-sm font-semibold text-neutral-700 mb-2">Positions</h4>
                        <div className="flex flex-wrap gap-2">
                          {(viewingTask.assignedToPositions && viewingTask.assignedToPositions.length > 0
                            ? viewingTask.assignedToPositions
                            : (viewingTask.assignedToPositionIds || []).map(positionId => ({ id: positionId, title: positions.find(p => p.id === positionId)?.title ?? '' })).filter(p => p.title)
                          ).map((position) => (
                              <AssignmentCircleLink
                                key={position.id}
                                kind="position"
                                label={position.title}
                                size="md"
                                onClick={() => openAssignmentEmployeesModal('position', position.id, position.title, viewingTask.locationId)}
                              />
                            ))}
                        </div>
                      </div>
                    )}
                    {/* Schedules */}
                    {((viewingTask.assignedToSchedules && viewingTask.assignedToSchedules.length > 0) || (viewingTask.assignedToScheduleIds && viewingTask.assignedToScheduleIds.length > 0)) && (
                      <div>
                        <h4 className="text-sm font-semibold text-neutral-700 mb-2">Schedules</h4>
                        <div className="flex flex-wrap gap-2">
                          {(viewingTask.assignedToSchedules && viewingTask.assignedToSchedules.length > 0
                            ? viewingTask.assignedToSchedules
                            : (viewingTask.assignedToScheduleIds || []).map(scheduleId => ({ id: scheduleId, name: schedules.find(s => s.id === scheduleId)?.name ?? '' })).filter(s => s.name)
                          ).map((schedule) => (
                              <AssignmentCircleLink
                                key={schedule.id}
                                kind="schedule"
                                label={schedule.name}
                                size="md"
                                onClick={() => openAssignmentEmployeesModal('schedule', schedule.id, schedule.name, viewingTask.locationId)}
                              />
                            ))}
                        </div>
                      </div>
                    )}
                    {(!viewingTask.assignedTo || viewingTask.assignedTo.length === 0) &&
                     (viewingTask.assignedToPositions?.length ?? viewingTask.assignedToPositionIds?.length ?? 0) === 0 &&
                     (viewingTask.assignedToSchedules?.length ?? viewingTask.assignedToScheduleIds?.length ?? 0) === 0 && (
                      <p className="text-sm text-neutral-400">No assignments</p>
                    )}
                  </div>
                </div>

                {/* Attachments */}
                {viewingTask.attachments && viewingTask.attachments.length > 0 && (
                  <div>
                    <label className="text-sm font-medium text-neutral-500 mb-3 block">Attachments</label>
                    <div className="space-y-2">
                      {viewingTask.attachments.map((attachment) => (
                        <a
                          key={attachment.id}
                          href={getUploadedFileUrl(attachment.filePath)}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="flex items-center gap-3 p-3 bg-neutral-50 border border-neutral-200 rounded-lg hover:bg-neutral-100 transition-colors"
                        >
                          <FileIcon className="h-5 w-5 text-blue-600 flex-shrink-0" />
                          <div className="flex-1 min-w-0">
                            <div className="text-sm font-medium text-neutral-900 truncate">
                              {attachment.fileName}
                            </div>
                            <div className="text-xs text-neutral-500">
                              {formatFileSize(attachment.fileSize)}
                            </div>
                          </div>
                          <Download className="h-4 w-4 text-neutral-400 flex-shrink-0" />
                        </a>
                      ))}
                    </div>
                  </div>
                )}

                {/* SubTasks Details */}
                {viewingSubTasks.length > 0 && (
                  <div>
                    <label className="text-sm font-medium text-neutral-500 mb-3 block">SubTasks ({viewingSubTasks.length})</label>
                    <div className="space-y-4">
                      {viewingSubTasks.map((subTask) => (
                        <div key={subTask.id} className="border border-neutral-200 rounded-lg p-4 bg-neutral-50">
                          <div className="flex justify-between items-start mb-3">
                            <div className="flex-1">
                              <h4 className="font-semibold text-neutral-800 mb-1">{subTask.name}</h4>
                              {subTask.description && (
                                <p className="text-sm text-neutral-600 whitespace-pre-wrap">{subTask.description}</p>
                              )}
                            </div>
                            <div className="flex items-center gap-2">
                              {canEditSubTask && (
                                <button
                                  onClick={() => {
                                    setShowTaskView(false);
                                    startEditSubTask(subTask);
                                  }}
                                  className="flex items-center gap-1 px-2 py-1 text-xs font-medium rounded bg-blue-600 text-white hover:bg-blue-700"
                                >
                                  <Edit className="h-3 w-3" />
                                  Edit
                                </button>
                              )}
                              <span className={`inline-flex items-center gap-1 px-3 py-1 rounded-full text-xs font-semibold border ${getStatusColor(subTask.status)}`}>
                                {getStatusIcon(subTask.status)}
                                {subTask.status}
                              </span>
                            </div>
                          </div>
                          
                          <div className="grid grid-cols-2 gap-3 mt-3 text-sm">
                            <div>
                              <label className="text-xs font-medium text-neutral-500">Due Date</label>
                              <p className="text-neutral-900 mt-1 flex items-center gap-1">
                                <Calendar className="h-3 w-3 text-neutral-400" />
                                {formatCompanyDate(subTask.dueDate)}
                              </p>
                            </div>
                            {subTask.status === 'Completed' && subTask.completedAt && (
                              <div>
                                <label className="text-xs font-medium text-neutral-500">Completed Date</label>
                                <p className="text-neutral-900 mt-1 flex items-center gap-1">
                                  <CheckCircle className="h-3 w-3 text-neutral-400" />
                                  {formatCompanyDate(subTask.completedAt)}
                                </p>
                              </div>
                            )}
                          </div>

                          {/* SubTask Assignments */}
                          {(subTask.assignedTo && subTask.assignedTo.length > 0) ||
                           (subTask.assignedToPositions?.length ?? subTask.assignedToPositionIds?.length ?? 0) > 0 ||
                           (subTask.assignedToSchedules?.length ?? subTask.assignedToScheduleIds?.length ?? 0) > 0 ? (
                            <div className="mt-3 pt-3 border-t border-neutral-200">
                              <label className="text-xs font-medium text-neutral-500 mb-2 block">Assigned To</label>
                              <div className="flex flex-wrap gap-2">
                                {/* Employees */}
                                {subTask.assignedTo && subTask.assignedTo.map((employee) => {
                                  const empProfileUrl = getProfileImageUrl((employee as { profileImageUrl?: string }).profileImageUrl ?? employees.find(e => e.id === employee.id)?.profileImageUrl, (employee as { updatedAt?: string }).updatedAt ?? employees.find(e => e.id === employee.id)?.updatedAt);
                                  return (
                                  <div key={employee.id} className="flex items-center gap-1 px-2 py-1 bg-blue-50 rounded text-xs">
                                    {empProfileUrl ? (
                                      <img src={empProfileUrl} alt={`${employee.firstName} ${employee.lastName}`} className="w-5 h-5 rounded-full object-cover" />
                                    ) : (
                                      <div className="w-5 h-5 rounded-full bg-blue-500 text-white flex items-center justify-center text-[10px] font-semibold">
                                        {`${employee.firstName?.[0] || ''}${employee.lastName?.[0] || ''}`.toUpperCase()}
                                      </div>
                                    )}
                                    <span className="text-neutral-900">{employee.firstName} {employee.lastName}</span>
                                  </div>
                                );})}
                                {/* Positions */}
                                {(subTask.assignedToPositions && subTask.assignedToPositions.length > 0
                                  ? subTask.assignedToPositions
                                  : (subTask.assignedToPositionIds || []).map(positionId => ({ id: positionId, title: positions.find(p => p.id === positionId)?.title ?? '' })).filter(p => p.title)
                                ).map((position) => (
                                    <AssignmentCircleLink
                                      key={position.id}
                                      kind="position"
                                      label={position.title}
                                      size="sm"
                                      onClick={() => openAssignmentEmployeesModal('position', position.id, position.title, viewingTask.locationId)}
                                    />
                                  ))}
                                {/* Schedules */}
                                {(subTask.assignedToSchedules && subTask.assignedToSchedules.length > 0
                                  ? subTask.assignedToSchedules
                                  : (subTask.assignedToScheduleIds || []).map(scheduleId => ({ id: scheduleId, name: schedules.find(s => s.id === scheduleId)?.name ?? '' })).filter(s => s.name)
                                ).map((schedule) => (
                                    <AssignmentCircleLink
                                      key={schedule.id}
                                      kind="schedule"
                                      label={schedule.name}
                                      size="sm"
                                      onClick={() => openAssignmentEmployeesModal('schedule', schedule.id, schedule.name, viewingTask.locationId)}
                                    />
                                  ))}
                              </div>
                            </div>
                          ) : null}

                          {/* SubTask Attachments */}
                          {subTask.attachments && subTask.attachments.length > 0 && (
                            <div className="mt-3 pt-3 border-t border-neutral-200">
                              <label className="text-xs font-medium text-neutral-500 mb-2 block">Attachments ({subTask.attachments.length})</label>
                              <div className="space-y-1">
                                {subTask.attachments.map((attachment) => (
                                  <a
                                    key={attachment.id}
                                    href={getUploadedFileUrl(attachment.filePath)}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="flex items-center gap-2 p-2 bg-white border border-neutral-200 rounded hover:bg-neutral-50 transition-colors text-xs"
                                  >
                                    <FileIcon className="h-3 w-3 text-blue-600 flex-shrink-0" />
                                    <span className="flex-1 min-w-0 truncate text-neutral-900">{attachment.fileName}</span>
                                    <span className="text-neutral-500 text-[10px]">{formatFileSize(attachment.fileSize)}</span>
                                    <Download className="h-3 w-3 text-neutral-400 flex-shrink-0" />
                                  </a>
                                ))}
                              </div>
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Sub-Task View Modal */}
      {showSubTaskView && viewingSubTask && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-3xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <div className="flex justify-between items-center mb-6">
                <h2 className="text-2xl font-bold text-neutral-800">Sub-Task Details</h2>
                <button
                  onClick={() => {
                    setShowSubTaskView(false);
                    setViewingSubTask(null);
                  }}
                  className="text-neutral-400 hover:text-neutral-600"
                >
                  <X className="h-5 w-5" />
                </button>
              </div>

              <div className="space-y-6">
                {/* Sub-Task Name */}
                <div>
                  <h3 className="text-lg font-semibold text-neutral-800 mb-2">{viewingSubTask.name}</h3>
                  {viewingSubTask.description && (
                    <p className="text-neutral-600 whitespace-pre-wrap">{viewingSubTask.description}</p>
                  )}
                </div>

                {/* Sub-Task Information Grid */}
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="text-sm font-medium text-neutral-500">Created By</label>
                    <p className="text-neutral-900 mt-1">
                      {viewingSubTask.createdBy?.firstName} {viewingSubTask.createdBy?.lastName}
                    </p>
                  </div>
                  <div>
                    <label className="text-sm font-medium text-neutral-500">Status</label>
                    <p className="text-neutral-900 mt-1">
                      <span className={`inline-flex items-center gap-1 px-3 py-1 rounded-full text-xs font-semibold border ${getStatusColor(viewingSubTask.status)}`}>
                        {getStatusIcon(viewingSubTask.status)}
                        {viewingSubTask.status}
                      </span>
                    </p>
                  </div>
                  <div>
                    <label className="text-sm font-medium text-neutral-500">Due Date</label>
                    <p className="text-neutral-900 mt-1 flex items-center gap-2">
                      <Calendar className="h-4 w-4 text-neutral-400" />
                      {formatCompanyDate(viewingSubTask.dueDate)}
                    </p>
                  </div>
                  {viewingSubTask.status === 'Completed' && viewingSubTask.completedAt && (
                    <div>
                      <label className="text-sm font-medium text-neutral-500">Completed Date</label>
                      <p className="text-neutral-900 mt-1 flex items-center gap-2">
                        <CheckCircle className="h-4 w-4 text-neutral-400" />
                        {formatCompanyDate(viewingSubTask.completedAt)}
                      </p>
                    </div>
                  )}
                  <div>
                    <label className="text-sm font-medium text-neutral-500">Created At</label>
                    <p className="text-neutral-900 mt-1">
                      {formatCompanyDateTime(viewingSubTask.createdAt)}
                    </p>
                  </div>
                  {viewingSubTask.updatedAt && (
                    <div>
                      <label className="text-sm font-medium text-neutral-500">Last Updated</label>
                      <p className="text-neutral-900 mt-1">
                        {formatCompanyDateTime(viewingSubTask.updatedAt)}
                      </p>
                    </div>
                  )}
                </div>

                {/* Assigned To */}
                <div>
                  <label className="text-sm font-medium text-neutral-500 mb-3 block">Assigned To</label>
                  <div className="space-y-3">
                    {/* Employees */}
                    {viewingSubTask.assignedTo && viewingSubTask.assignedTo.length > 0 && (
                      <div>
                        <h4 className="text-sm font-semibold text-neutral-700 mb-2">Employees</h4>
                        <div className="flex flex-wrap gap-2">
                          {viewingSubTask.assignedTo.map((employee) => {
                            const empProfileUrl = getProfileImageUrl((employee as { profileImageUrl?: string }).profileImageUrl ?? employees.find(e => e.id === employee.id)?.profileImageUrl, (employee as { updatedAt?: string }).updatedAt ?? employees.find(e => e.id === employee.id)?.updatedAt);
                            return (
                            <div key={employee.id} className="flex items-center gap-2 px-3 py-2 bg-blue-50 rounded-lg">
                              {empProfileUrl ? (
                                <img src={empProfileUrl} alt={`${employee.firstName} ${employee.lastName}`} className="w-8 h-8 rounded-full object-cover" />
                              ) : (
                                <div className="w-8 h-8 rounded-full bg-blue-500 text-white flex items-center justify-center text-xs font-semibold">
                                  {`${employee.firstName?.[0] || ''}${employee.lastName?.[0] || ''}`.toUpperCase()}
                                </div>
                              )}
                              <span className="text-sm text-neutral-900">
                                {employee.firstName} {employee.lastName}
                              </span>
                            </div>
                          );})}
                        </div>
                      </div>
                    )}
                    {/* Positions */}
                    {((viewingSubTask.assignedToPositions && viewingSubTask.assignedToPositions.length > 0) || (viewingSubTask.assignedToPositionIds && viewingSubTask.assignedToPositionIds.length > 0)) && (
                      <div>
                        <h4 className="text-sm font-semibold text-neutral-700 mb-2">Positions</h4>
                        <div className="flex flex-wrap gap-2">
                          {(viewingSubTask.assignedToPositions && viewingSubTask.assignedToPositions.length > 0
                            ? viewingSubTask.assignedToPositions
                            : (viewingSubTask.assignedToPositionIds || []).map(positionId => ({ id: positionId, title: positions.find(p => p.id === positionId)?.title ?? '' })).filter(p => p.title)
                          ).map((position) => (
                              <AssignmentCircleLink
                                key={position.id}
                                kind="position"
                                label={position.title}
                                size="md"
                                onClick={() => openAssignmentEmployeesModal('position', position.id, position.title, viewingTask?.locationId)}
                              />
                            ))}
                        </div>
                      </div>
                    )}
                    {/* Schedules */}
                    {((viewingSubTask.assignedToSchedules && viewingSubTask.assignedToSchedules.length > 0) || (viewingSubTask.assignedToScheduleIds && viewingSubTask.assignedToScheduleIds.length > 0)) && (
                      <div>
                        <h4 className="text-sm font-semibold text-neutral-700 mb-2">Schedules</h4>
                        <div className="flex flex-wrap gap-2">
                          {(viewingSubTask.assignedToSchedules && viewingSubTask.assignedToSchedules.length > 0
                            ? viewingSubTask.assignedToSchedules
                            : (viewingSubTask.assignedToScheduleIds || []).map(scheduleId => ({ id: scheduleId, name: schedules.find(s => s.id === scheduleId)?.name ?? '' })).filter(s => s.name)
                          ).map((schedule) => (
                              <AssignmentCircleLink
                                key={schedule.id}
                                kind="schedule"
                                label={schedule.name}
                                size="md"
                                onClick={() => openAssignmentEmployeesModal('schedule', schedule.id, schedule.name, viewingTask?.locationId)}
                              />
                            ))}
                        </div>
                      </div>
                    )}
                    {(!viewingSubTask.assignedTo || viewingSubTask.assignedTo.length === 0) &&
                     (viewingSubTask.assignedToPositions?.length ?? viewingSubTask.assignedToPositionIds?.length ?? 0) === 0 &&
                     (viewingSubTask.assignedToSchedules?.length ?? viewingSubTask.assignedToScheduleIds?.length ?? 0) === 0 && (
                      <p className="text-sm text-neutral-400">No assignments</p>
                    )}
                  </div>
                </div>

                {/* Attachments */}
                {viewingSubTask.attachments && viewingSubTask.attachments.length > 0 && (
                  <div>
                    <label className="text-sm font-medium text-neutral-500 mb-3 block">Attachments</label>
                    <div className="space-y-2">
                      {viewingSubTask.attachments.map((attachment) => (
                        <a
                          key={attachment.id}
                          href={getUploadedFileUrl(attachment.filePath)}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="flex items-center gap-3 p-3 bg-neutral-50 border border-neutral-200 rounded-lg hover:bg-neutral-100 transition-colors"
                        >
                          <FileIcon className="h-5 w-5 text-blue-600 flex-shrink-0" />
                          <div className="flex-1 min-w-0">
                            <div className="text-sm font-medium text-neutral-900 truncate">
                              {attachment.fileName}
                            </div>
                            <div className="text-xs text-neutral-500">
                              {formatFileSize(attachment.fileSize)}
                            </div>
                          </div>
                          <Download className="h-4 w-4 text-neutral-400 flex-shrink-0" />
                        </a>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Sub-Tasks Panel */}
      {selectedTask && !showChat && !showSubTaskChat && (
        <div className="fixed right-0 top-0 h-full w-96 bg-white shadow-xl border-l border-neutral-200 z-40 flex flex-col">
          <div className="p-4 border-b border-neutral-200 flex justify-between items-center">
            <div>
              <h2 className="text-lg font-bold text-neutral-800">Sub-Tasks</h2>
              <p className="text-sm text-neutral-500">{selectedTask.name}</p>
            </div>
            <div className="flex gap-2">
              <button
                onClick={() => {
                  resetSubTaskForm();
                  setShowSubTaskForm(true);
                }}
                className="p-2 text-blue-600 hover:bg-blue-50 rounded"
                title="Add Sub-Task"
              >
                <Plus className="h-5 w-5" />
              </button>
              <button
                onClick={() => {
                  setSelectedTask(null);
                  setSubTasks([]);
                }}
                className="text-neutral-400 hover:text-neutral-600"
              >
                <X className="h-5 w-5" />
              </button>
            </div>
          </div>

          <div className="flex-1 overflow-y-auto p-4">
            {subTasks.length === 0 ? (
              <div className="text-center text-neutral-500 py-8">
                No sub-tasks yet. Click the + button to add one.
              </div>
            ) : (
              <div className="space-y-3">
                {subTasks.map((subTask) => (
                  <div
                    key={subTask.id}
                    className="border border-neutral-200 rounded-lg p-3 hover:shadow-md transition-shadow"
                  >
                    <div className="flex justify-between items-start mb-2">
                      <h3 className="font-semibold text-neutral-800">{subTask.name}</h3>
                      <div className="flex gap-1">
                        <button
                          onClick={() => openSubTaskChat(subTask)}
                          className="p-1 text-blue-600 hover:bg-blue-50 rounded relative"
                          title="View Chat"
                        >
                          <MessageSquare className="h-4 w-4" />
                          {subTask.conversationsCount !== undefined && subTask.conversationsCount > 0 && (
                            <span className="absolute -top-2 -right-2 bg-blue-600 text-white text-xs rounded-full h-5 w-5 flex items-center justify-center border-2 border-white">
                              {subTask.conversationsCount}
                            </span>
                          )}
                        </button>
                        <button
                          onClick={async () => {
                            // Fetch full sub-task details with all related data
                            if (selectedTask?.id && subTask.id) {
                              try {
                                const fullSubTask = await subTaskApi.getById(selectedTask.id, subTask.id);
                                setViewingSubTask(fullSubTask as SubTask);
                                setShowSubTaskView(true);
                              } catch (error) {
                                console.error('Error loading sub-task details:', error);
                                // Fallback to using the subTask from the list
                                setViewingSubTask(subTask);
                                setShowSubTaskView(true);
                              }
                            } else {
                              setViewingSubTask(subTask);
                              setShowSubTaskView(true);
                            }
                          }}
                          className="p-1 text-purple-600 hover:bg-purple-50 rounded"
                          title="View"
                        >
                          <Eye className="h-4 w-4" />
                        </button>
                        {!(myTasksTaskId !== null && selectedTask?.id === myTasksTaskId && !canEditSubTask) && (
                        <button
                          onClick={() => startEditSubTask(subTask)}
                          className="p-1 text-neutral-600 hover:bg-neutral-100 rounded"
                          title="Edit"
                        >
                          <Edit className="h-4 w-4" />
                        </button>
                        )}
                        {hasSuperAdminRole && (
                        <button
                          onClick={() => handleDeleteSubTask(subTask.id)}
                          className="p-1 text-red-600 hover:bg-red-50 rounded"
                          title="Delete (Super Admin only)"
                        >
                          <Trash2 className="h-4 w-4" />
                        </button>
                        )}
                      </div>
                    </div>
                    {subTask.description && (
                      <p className="text-sm text-neutral-600 mb-2">{subTask.description}</p>
                    )}
                    <div className="flex items-center gap-4 text-xs text-neutral-500 mb-2">
                      <span className={`px-2 py-1 rounded ${
                        subTask.status === 'Completed' ? 'bg-green-100 text-green-700' :
                        subTask.status === 'InProgress' ? 'bg-blue-100 text-blue-700' :
                        'bg-neutral-100 text-neutral-700'
                      }`}>
                        {subTask.status}
                      </span>
                      <span className="flex items-center gap-1">
                        <Calendar className="h-3 w-3" />
                        {formatCompanyDate(subTask.dueDate)}
                      </span>
                      {subTask.status === 'Completed' && subTask.completedAt && (
                        <span className="flex items-center gap-1 text-green-600">
                          <CheckCircle className="h-3 w-3" />
                          Completed: {formatCompanyDate(subTask.completedAt)}
                        </span>
                      )}
                    </div>
                    <div className="flex items-center gap-2 flex-wrap">
                      {subTask.assignedTo && subTask.assignedTo.length > 0 && (
                        <>
                          {subTask.assignedTo.map((employee) => {
                            const initials = `${employee.firstName?.[0] || ''}${employee.lastName?.[0] || ''}`.toUpperCase();
                            const fullName = `${employee.firstName} ${employee.lastName}`;
                            const empProfileUrl = getProfileImageUrl((employee as { profileImageUrl?: string }).profileImageUrl ?? employees.find(e => e.id === employee.id)?.profileImageUrl, (employee as { updatedAt?: string }).updatedAt ?? employees.find(e => e.id === employee.id)?.updatedAt);
                            return (
                              <div key={employee.id} className="relative group">
                                {empProfileUrl ? (
                                  <img src={empProfileUrl} alt={fullName} className="w-6 h-6 rounded-full object-cover cursor-pointer hover:ring-2 hover:ring-blue-600 transition-colors" />
                                ) : (
                                  <div className="w-6 h-6 rounded-full bg-blue-500 text-white flex items-center justify-center text-[10px] font-semibold cursor-pointer hover:bg-blue-600 transition-colors">
                                    {initials}
                                  </div>
                                )}
                                <div className="absolute left-0 bottom-full mb-1 hidden group-hover:block z-10">
                                  <div className="bg-neutral-800 text-white text-xs rounded py-1 px-2 whitespace-nowrap">{fullName}</div>
                                  <div className="w-0 h-0 border-l-4 border-r-4 border-t-4 border-transparent border-t-neutral-800 ml-2"></div>
                                </div>
                              </div>
                            );
                          })}
                        </>
                      )}
                      {(subTask.assignedToPositions && subTask.assignedToPositions.length > 0
                        ? subTask.assignedToPositions
                        : (subTask.assignedToPositionIds || []).map(positionId => ({ id: positionId, title: positions.find(p => p.id === positionId)?.title ?? '' })).filter(p => p.title)
                      ).map((position) => (
                        <AssignmentCircleLink
                          key={`pos-${position.id}`}
                          kind="position"
                          label={position.title}
                          size="sm"
                          onClick={() => openAssignmentEmployeesModal('position', position.id, position.title, selectedTask?.locationId)}
                        />
                      ))}
                      {((subTask.assignedToSchedules && subTask.assignedToSchedules.length > 0)
                        ? subTask.assignedToSchedules
                        : (subTask.assignedToScheduleIds || []).map(scheduleId => ({ id: scheduleId, name: schedules.find(s => s.id === scheduleId)?.name ?? '' })).filter(s => s.name)
                      ).map((schedule) => (
                        <AssignmentCircleLink
                          key={`sched-${schedule.id}`}
                          kind="schedule"
                          label={schedule.name}
                          size="sm"
                          onClick={() => openAssignmentEmployeesModal('schedule', schedule.id, schedule.name, selectedTask?.locationId)}
                        />
                      ))}
                      {(!subTask.assignedTo || subTask.assignedTo.length === 0) &&
                       (subTask.assignedToPositions?.length ?? subTask.assignedToPositionIds?.length ?? 0) === 0 &&
                       (subTask.assignedToSchedules?.length ?? subTask.assignedToScheduleIds?.length ?? 0) === 0 && (
                        <span className="text-xs text-neutral-400">Unassigned</span>
                      )}
                    </div>
                  </div>
                    ))}
                  </div>
            )}
                </div>
        </div>
      )}

      {/* Sub-Task Form Modal */}
      {showSubTaskForm && selectedTask && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <div className="flex justify-between items-center mb-6">
                <h2 className="text-xl font-bold text-neutral-800">
                  {editingSubTask ? 'Edit Sub-Task' : 'Create New Sub-Task'}
                </h2>
                <button
                  onClick={() => {
                    setShowSubTaskForm(false);
                    resetSubTaskForm();
                  }}
                  className="text-neutral-400 hover:text-neutral-600"
                >
                  <X className="h-5 w-5" />
                </button>
              </div>

              <form onSubmit={handleSubTaskSubmit} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-neutral-700 mb-1">
                    Name <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    value={subTaskFormData.name}
                    onChange={(e) => setSubTaskFormData({ ...subTaskFormData, name: e.target.value })}
                    className="w-full px-3 py-2 border border-neutral-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-neutral-700 mb-1">
                    Description
                  </label>
                  <textarea
                    value={subTaskFormData.description || ''}
                    onChange={(e) => setSubTaskFormData({ ...subTaskFormData, description: e.target.value })}
                    className="w-full px-3 py-2 border border-neutral-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    rows={4}
                  />
                </div>

                {isLimitedSubTask && editingSubTask && (
                  <div className="px-3 py-2 rounded-lg bg-amber-50 border border-amber-200 text-amber-800 text-sm">
                    Limited access: You cannot change assignments, due date, or status.
                  </div>
                )}

                <div>
                  <label className="block text-sm font-medium text-neutral-700 mb-2">
                    Assigned To <span className="text-red-500">*</span>
                  </label>
                  
                  <div className={`flex gap-2 mb-3 border-b border-neutral-200 ${isLimitedSubTask ? 'opacity-60 pointer-events-none' : ''}`}>
                    <button
                      type="button"
                      onClick={() => setSubTaskAssignedToCategory('employees')}
                      className={`px-4 py-2 text-sm font-medium transition-colors ${
                        subTaskAssignedToCategory === 'employees'
                          ? 'text-blue-600 border-b-2 border-blue-600'
                          : 'text-neutral-600 hover:text-neutral-900'
                      }`}
                    >
                      Employees ({subTaskFormData.assignedToEmployeeIds.length})
                    </button>
                    <button
                      type="button"
                      onClick={() => setSubTaskAssignedToCategory('positions')}
                      className={`px-4 py-2 text-sm font-medium transition-colors ${
                        subTaskAssignedToCategory === 'positions'
                          ? 'text-blue-600 border-b-2 border-blue-600'
                          : 'text-neutral-600 hover:text-neutral-900'
                      }`}
                    >
                      Positions ({(subTaskFormData.assignedToPositionIds || []).length})
                    </button>
                    <button
                      type="button"
                      onClick={() => setSubTaskAssignedToCategory('schedules')}
                      className={`px-4 py-2 text-sm font-medium transition-colors ${
                        subTaskAssignedToCategory === 'schedules'
                          ? 'text-blue-600 border-b-2 border-blue-600'
                          : 'text-neutral-600 hover:text-neutral-900'
                      }`}
                    >
                      Schedules ({(subTaskFormData.assignedToScheduleIds || []).length})
                    </button>
                  </div>

                  {subTaskAssignedToCategory === 'employees' && (
                    <div className={isLimitedSubTask ? 'opacity-60 pointer-events-none' : ''}>
                      <BasketSelect
                        label=""
                        availableItems={employeesFilteredByLocation.map(emp => ({
                          id: emp.id,
                          label: `${emp.firstName} ${emp.lastName} (${emp.email})`
                        }))}
                        extraSelectedItems={employeeBasketExtras(editingSubTask ?? {})}
                        selectedIds={subTaskFormData.assignedToEmployeeIds}
                        onChange={(ids) => setSubTaskFormData({ ...subTaskFormData, assignedToEmployeeIds: ids as number[] })}
                        placeholder="Select employees..."
                      />
                    </div>
                  )}

                  {subTaskAssignedToCategory === 'positions' && (
                    <div className={isLimitedSubTask ? 'opacity-60 pointer-events-none' : ''}>
                      <BasketSelect
                        label=""
                        availableItems={positions.map(pos => ({
                          id: pos.id,
                          label: pos.title
                        }))}
                        extraSelectedItems={positionBasketExtras(editingSubTask ?? {})}
                        selectedIds={subTaskFormData.assignedToPositionIds || []}
                        onChange={(ids) => setSubTaskFormData({ ...subTaskFormData, assignedToPositionIds: ids as number[] })}
                        placeholder="Select positions..."
                      />
                    </div>
                  )}

                  {subTaskAssignedToCategory === 'schedules' && (
                    <div className={isLimitedSubTask ? 'opacity-60 pointer-events-none' : ''}>
                      <BasketSelect
                      label=""
                      availableItems={schedulesFilteredByLocation.map(schedule => ({
                        id: schedule.id,
                        label: schedule.name
                      }))}
                      extraSelectedItems={scheduleBasketExtras(editingSubTask ?? {})}
                      selectedIds={subTaskFormData.assignedToScheduleIds || []}
                      onChange={(ids) => setSubTaskFormData({ ...subTaskFormData, assignedToScheduleIds: ids as number[] })}
                      placeholder="Select schedules..."
                    />
                    </div>
                  )}
                </div>

                <div className={isLimitedSubTask ? 'opacity-60 pointer-events-none' : ''}>
                  <label className="block text-sm font-medium text-neutral-700 mb-1">
                    Due Date <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="date"
                    value={subTaskFormData.dueDate}
                    onChange={(e) => setSubTaskFormData({ ...subTaskFormData, dueDate: e.target.value })}
                    className="w-full px-3 py-2 border border-neutral-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    required
                  />
                </div>

                <div className={isLimitedSubTask ? 'opacity-60 pointer-events-none' : ''}>
                  <label className="block text-sm font-medium text-neutral-700 mb-1">
                    Status
                  </label>
                  <select
                    value={subTaskFormData.status}
                    onChange={(e) => setSubTaskFormData({ ...subTaskFormData, status: e.target.value as any })}
                    className="w-full px-3 py-2 border border-neutral-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="Not Started">Not Started</option>
                    <option value="InProgress">In Progress</option>
                    <option value="Completed">Completed</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-neutral-700 mb-1">
                    Attachments
                  </label>
                  <input
                    type="file"
                    multiple
                    onChange={(e) => {
                      if (e.target.files && e.target.files.length > 0) {
                        const newFiles = Array.from(e.target.files);
                        console.log('[SubTask Form] Files selected:', newFiles.map(f => ({ name: f.name, size: f.size, type: f.type })));
                        setSubTaskFormFiles([...subTaskFormFiles, ...newFiles]);
                      }
                    }}
                    className="w-full px-3 py-2 border border-neutral-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                  
                  {/* Existing Attachments */}
                  {existingSubTaskAttachments.length > 0 && (
                    <div className="mt-2 space-y-1">
                      <div className="text-xs font-medium text-neutral-500">Existing Attachments:</div>
                      {existingSubTaskAttachments.map((attachment) => (
                        <div key={attachment.id} className="flex items-center gap-2 px-2 py-1 bg-neutral-50 border border-neutral-200 rounded text-sm">
                          <FileIcon className="h-4 w-4 text-neutral-600" />
                          <span className="text-neutral-700 flex-1 truncate">{attachment.fileName}</span>
                          <span className="text-xs text-neutral-500">({formatFileSize(attachment.fileSize)})</span>
                          <button
                            type="button"
                            onClick={() => {
                              setSubTaskAttachmentsToDelete([...subTaskAttachmentsToDelete, attachment.id]);
                              setExistingSubTaskAttachments(existingSubTaskAttachments.filter(a => a.id !== attachment.id));
                            }}
                            className="text-red-600 hover:text-red-800"
                            title="Remove attachment"
                          >
                            <X className="h-4 w-4" />
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                  
                  {/* New Files */}
                  {subTaskFormFiles.length > 0 && (
                    <div className="mt-2 space-y-1">
                      <div className="text-xs font-medium text-neutral-500">New Files:</div>
                      <div className="flex flex-wrap gap-2">
                        {subTaskFormFiles.map((file, index) => (
                          <div key={index} className="flex items-center gap-2 px-2 py-1 bg-blue-50 border border-blue-200 rounded text-sm">
                            <FileIcon className="h-4 w-4 text-blue-600" />
                            <span className="text-neutral-700">{file.name}</span>
                            <button
                              type="button"
                              onClick={() => setSubTaskFormFiles(subTaskFormFiles.filter((_, i) => i !== index))}
                              className="text-red-600 hover:text-red-800"
                            >
                              <X className="h-4 w-4" />
                            </button>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>

                <div className="flex justify-end gap-3 pt-4">
                  <button
                    type="button"
                    onClick={() => {
                      setShowSubTaskForm(false);
                      resetSubTaskForm();
                    }}
                    className="px-4 py-2 border border-neutral-300 rounded-md text-neutral-700 hover:bg-neutral-50"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={saving}
                    className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50"
                  >
                    {saving ? 'Saving...' : editingSubTask ? 'Update' : 'Create'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* Sub-Task Chat Modal */}
      {showSubTaskChat && selectedTask && selectedSubTask && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-3xl w-full h-[80vh] flex flex-col">
            <div className="p-4 border-b border-neutral-200 flex justify-between items-center">
              <div>
                <h2 className="text-lg font-bold text-neutral-800">{selectedSubTask.name}</h2>
                <p className="text-sm text-neutral-500">Sub-Task Conversations</p>
              </div>
              <button
                onClick={() => {
                  setShowSubTaskChat(false);
                  setSelectedSubTask(null);
                }}
                className="text-neutral-400 hover:text-neutral-600"
              >
                <X className="h-5 w-5" />
              </button>
            </div>

            <div 
              ref={subTaskChatScrollContainerRef}
              className="flex-1 overflow-y-auto p-4 space-y-4 bg-neutral-50"
            >
              {selectedSubTask.conversations && selectedSubTask.conversations.length > 0 ? (
                selectedSubTask.conversations.map((conv) => {
                  const hasMessage = conv.message && conv.message.trim();
                  const hasAttachments = conv.attachments && conv.attachments.length > 0;
                  
                  return (
                    <div 
                      key={conv.id} 
                      ref={(el) => { if (el) subTaskConversationRefs.current[conv.id] = el; }}
                      className="group flex gap-3"
                    >
                      <div className="flex-shrink-0">
                        {(() => {
                          const emp = conv.employee;
                          const empProfileUrl = emp && getProfileImageUrl((emp as { profileImageUrl?: string }).profileImageUrl ?? employees.find(e => e.id === emp.id)?.profileImageUrl, (emp as { updatedAt?: string }).updatedAt ?? employees.find(e => e.id === emp.id)?.updatedAt);
                          return empProfileUrl ? (
                            <img src={empProfileUrl} alt={`${emp.firstName} ${emp.lastName}`} className="w-8 h-8 rounded-full object-cover" />
                          ) : (
                            <div className="w-8 h-8 rounded-full bg-blue-600 flex items-center justify-center text-white text-sm font-medium">
                              {emp?.firstName?.[0] || 'U'}
                            </div>
                          );
                        })()}
                      </div>
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-1">
                          <span className="font-medium text-sm text-neutral-900">
                            {conv.employee?.firstName} {conv.employee?.lastName}
                          </span>
                          <span className="text-xs text-neutral-500">
                            {formatCompanyDateTime(conv.createdAt)}
                          </span>
                        </div>
                        
                        {conv.replyTo && (
                          <div 
                            className="mb-2 p-2 bg-neutral-100 border-l-2 border-blue-500 rounded text-xs cursor-pointer hover:bg-neutral-200 transition-colors"
                            onClick={(e) => {
                              e.stopPropagation();
                              const originalConvId = conv.replyTo?.id;
                              if (originalConvId) {
                                // Wait a bit for DOM to be ready, then scroll
                                setTimeout(() => {
                                  const originalElement = subTaskConversationRefs.current[originalConvId];
                                  const scrollContainer = subTaskChatScrollContainerRef.current;
                                  if (originalElement && scrollContainer) {
                                    // Calculate position relative to scroll container
                                    const containerRect = scrollContainer.getBoundingClientRect();
                                    const elementRect = originalElement.getBoundingClientRect();
                                    const scrollTop = scrollContainer.scrollTop;
                                    const elementTop = elementRect.top - containerRect.top + scrollTop;
                                    
                                    // Scroll to center the element
                                    scrollContainer.scrollTo({
                                      top: elementTop - (scrollContainer.clientHeight / 2) + (elementRect.height / 2),
                                      behavior: 'smooth'
                                    });
                                    
                                    // Highlight briefly
                                    originalElement.classList.add('ring-2', 'ring-blue-400');
                                    setTimeout(() => {
                                      originalElement.classList.remove('ring-2', 'ring-blue-400');
                                    }, 2000);
                                  } else if (originalElement) {
                                    // Fallback to scrollIntoView
                                    originalElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
                                  } else {
                                    console.warn('[Reply Preview] Original conversation element not found:', originalConvId);
                                  }
                                }, 100);
                              }
                            }}
                          >
                            <div className="font-semibold text-neutral-800 mb-1">
                              {conv.replyTo.employee.firstName} {conv.replyTo.employee.lastName}
                            </div>
                            <div className="text-neutral-600 line-clamp-2">{conv.replyTo.message}</div>
                          </div>
                        )}
                        
                        {((hasMessage) || hasAttachments) && (
                          <div className="bg-white rounded-lg p-3 shadow-sm border border-neutral-200 relative">
                            {hasMessage && (
                              <p className="text-sm text-neutral-700 whitespace-pre-wrap">{conv.message}</p>
                            )}
                            
                            {/* Attachments */}
                            {hasAttachments && conv.attachments && (
                              <div className={`space-y-2 ${hasMessage ? 'mt-2' : ''}`}>
                                {conv.attachments.map((attachment) => {
                                  const isAudio = attachment.contentType?.startsWith('audio/') || 
                                                  attachment.fileName?.endsWith('.webm') || 
                                                  attachment.fileName?.endsWith('.mp3') || 
                                                  attachment.fileName?.endsWith('.wav') ||
                                                  attachment.fileName?.endsWith('.m4a') ||
                                                  attachment.fileName?.endsWith('.aac') ||
                                                  attachment.fileName?.startsWith('voice-message-') ||
                                                  attachment.fileName?.startsWith('task_admin_chat_');
                                  
                                  if (isAudio) {
                                    return (
                                      <div
                                        key={attachment.id}
                                        className="flex items-center gap-2 p-2 bg-neutral-50 border border-neutral-200 rounded"
                                      >
                                        <Mic className="h-4 w-4 text-purple-600 flex-shrink-0" />
                                        <ConversationVoiceAttachment
                                          openHref={getAttachmentUrl(
                                            attachment.fileUrl ?? attachment.filePath,
                                            'subtask-conversations'
                                          )}
                                          playbackUrl={
                                            selectedTask && selectedSubTask
                                              ? getSubTaskConversationAttachmentPlaybackUrl(
                                                  selectedTask.id,
                                                  selectedSubTask.id,
                                                  conv.id,
                                                  attachment.id
                                                )
                                              : null
                                          }
                                          fileName={attachment.fileName}
                                        />
                                      </div>
                                    );
                                  }
                                  
                                  return (
                                    <a
                                      key={attachment.id}
                                      href={getAttachmentUrl(
                                        attachment.fileUrl ?? attachment.filePath,
                                        'subtask-conversations'
                                      )}
                                      target="_blank"
                                      rel="noopener noreferrer"
                                      className="flex items-center gap-2 p-2 bg-neutral-50 border border-neutral-200 rounded hover:bg-neutral-100 transition-colors"
                                    >
                                      <FileIcon className="h-4 w-4 text-blue-600 flex-shrink-0" />
                                      <div className="flex-1 min-w-0">
                                        <div className="text-sm font-medium text-neutral-900 truncate">
                                          {attachment.fileName}
                                        </div>
                                        <div className="text-xs text-neutral-500">
                                          {formatFileSize(attachment.fileSize)}
                                        </div>
                                      </div>
                                      <Download className="h-4 w-4 text-neutral-400 flex-shrink-0" />
                                    </a>
                                  );
                                })}
                              </div>
                            )}
                            
                          {/* Reactions */}
                          {conv.reactions && conv.reactions.length > 0 && (
                            <div className="mt-2 flex flex-wrap gap-1">
                              {conv.reactions.map((reaction, idx) => (
                                <div key={idx} className="relative">
                                  <button
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      setShowReactionDetails({
                                        conversationId: conv.id,
                                        reaction,
                                        isSubTask: true
                                      });
                                    }}
                                    className={`flex items-center gap-1 px-2 py-1 rounded-full text-xs border transition-colors ${
                                      reaction.employees.some(e => e.id === currentEmployee?.id)
                                        ? 'bg-blue-50 border-blue-300 text-blue-700'
                                        : 'bg-neutral-50 border-neutral-200 text-neutral-700 hover:bg-neutral-100'
                                    }`}
                                  >
                                    <span>{getReactionEmoji(reaction.type)}</span>
                                    <span>{reaction.count}</span>
                                  </button>
                                </div>
                              ))}
                            </div>
                          )}
                            
                            {/* Action Buttons */}
                            <div className="mt-2 flex items-center gap-2 opacity-0 group-hover:opacity-100 transition-opacity">
                              <button
                                onClick={() => setReplyingToSubTask(conv)}
                                className="flex items-center gap-1 px-2 py-1 text-xs text-neutral-600 hover:text-blue-600 hover:bg-blue-50 rounded transition-colors"
                              >
                                <Reply className="h-3 w-3" />
                                Reply
                              </button>
                              <div className="relative">
                                <button
                                  onClick={() => setShowSubTaskReactionPicker(showSubTaskReactionPicker === conv.id ? null : conv.id)}
                                  className="flex items-center gap-1 px-2 py-1 text-xs text-neutral-600 hover:text-blue-600 hover:bg-blue-50 rounded transition-colors"
                                >
                                  <Smile className="h-3 w-3" />
                                  React
                                </button>
                                
                                {/* Reaction Picker */}
                                {showSubTaskReactionPicker === conv.id && (
                                  <div className="absolute bottom-full left-0 mb-2 bg-white border border-neutral-200 rounded-lg shadow-lg p-2 flex gap-1 z-10">
                                    {['like', 'love', 'laugh', 'wow', 'sad', 'angry'].map((type) => (
                                      <button
                                        key={type}
                                        onClick={() => handleSubTaskReaction(conv.id, type)}
                                        className="p-2 hover:bg-neutral-100 rounded transition-colors text-lg"
                                        title={type.charAt(0).toUpperCase() + type.slice(1)}
                                      >
                                        {getReactionEmoji(type)}
                                      </button>
                                    ))}
                                  </div>
                                )}
                              </div>
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  );
                })
              ) : (
                <div className="text-center text-neutral-500 py-8">
                  No conversations yet. Start the conversation!
                </div>
              )}
              <div ref={subTaskChatEndRef} />
            </div>

            <form onSubmit={handleSendSubTaskMessage} className="p-4 border-t border-neutral-200 bg-white">
              {/* Reply To Preview */}
              {replyingToSubTask && (
                <div className="mb-2 p-2 bg-blue-50 border-l-4 border-blue-400 rounded-md flex items-start gap-2">
                  <div 
                    className="flex-1 cursor-pointer"
                    onClick={(e) => {
                      e.stopPropagation();
                      const originalConvId = replyingToSubTask.id;
                      if (originalConvId) {
                        // Wait a bit for DOM to be ready, then scroll
                        setTimeout(() => {
                          const originalElement = subTaskConversationRefs.current[originalConvId];
                          const scrollContainer = subTaskChatScrollContainerRef.current;
                          if (originalElement && scrollContainer) {
                            // Calculate position relative to scroll container
                            const containerRect = scrollContainer.getBoundingClientRect();
                            const elementRect = originalElement.getBoundingClientRect();
                            const scrollTop = scrollContainer.scrollTop;
                            const elementTop = elementRect.top - containerRect.top + scrollTop;
                            
                            // Scroll to center the element
                            scrollContainer.scrollTo({
                              top: elementTop - (scrollContainer.clientHeight / 2) + (elementRect.height / 2),
                              behavior: 'smooth'
                            });
                            
                            // Highlight briefly
                            originalElement.classList.add('ring-2', 'ring-blue-400');
                            setTimeout(() => {
                              originalElement.classList.remove('ring-2', 'ring-blue-400');
                            }, 2000);
                          } else if (originalElement) {
                            // Fallback to scrollIntoView
                            originalElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
                          } else {
                            console.warn('[Reply Preview] Original conversation element not found:', originalConvId);
                          }
                        }, 100);
                      }
                    }}
                  >
                    <div className="text-xs font-semibold text-blue-800 mb-1">
                      {replyingToSubTask.employee?.firstName} {replyingToSubTask.employee?.lastName}
                    </div>
                    <div className="text-xs text-neutral-600 line-clamp-2">{replyingToSubTask.message}</div>
                  </div>
                  <button
                    type="button"
                    onClick={() => setReplyingToSubTask(null)}
                    className="text-neutral-400 hover:text-neutral-600 flex-shrink-0"
                  >
                    <X className="h-4 w-4" />
                  </button>
                </div>
              )}

              {/* Selected Files Preview */}
              {selectedSubTaskFiles.length > 0 && (
                <div className="mb-2 flex flex-wrap gap-2">
                  {selectedSubTaskFiles.map((file, index) => (
                    <div
                      key={index}
                      className="flex items-center gap-2 px-3 py-2 bg-blue-50 border border-blue-200 rounded-md"
                    >
                      {file.name.startsWith('voice-message-') || file.type.startsWith('audio/') ? (
                        <Mic className="h-4 w-4 text-purple-600 flex-shrink-0" />
                      ) : (
                        <FileIcon className="h-4 w-4 text-blue-600 flex-shrink-0" />
                      )}
                      <span className="text-sm text-neutral-700 truncate max-w-[200px]">
                        {file.name.startsWith('voice-message-') ? 'Voice Message' : file.name}
                      </span>
                      <span className="text-xs text-neutral-500">
                        {formatFileSize(file.size)}
                      </span>
                      <button
                        type="button"
                        onClick={() => removeSubTaskFile(index)}
                        className="text-neutral-400 hover:text-red-600 flex-shrink-0"
                      >
                        <X className="h-4 w-4" />
                      </button>
                    </div>
                  ))}
                </div>
              )}
              
              <div className="flex gap-2">
                <input
                  ref={subTaskFileInputRef}
                  type="file"
                  multiple
                  onChange={handleSubTaskFileSelect}
                  className="hidden"
                  id="subtask-file-input"
                />
                <label
                  htmlFor="subtask-file-input"
                  className="px-3 py-2 border border-neutral-300 rounded-md hover:bg-neutral-50 cursor-pointer flex items-center justify-center transition-colors"
                  title="Attach files"
                >
                  <Paperclip className="h-5 w-5 text-neutral-600" />
                </label>
                {isRecordingSubTask ? (
                  <button
                    type="button"
                    onClick={stopRecordingSubTask}
                    className="px-3 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 flex items-center gap-2"
                    title="Stop recording"
                  >
                    <Square className="h-4 w-4" />
                    <span className="text-sm">{formatRecordingTime(recordingTimeSubTask)}</span>
                  </button>
                ) : (
                  <button
                    type="button"
                    onClick={startRecordingSubTask}
                    className="px-3 py-2 border border-neutral-300 rounded-md hover:bg-neutral-50 flex items-center justify-center transition-colors"
                    title="Record voice message"
                  >
                    <Mic className="h-5 w-5 text-purple-600" />
                  </button>
                )}
                <div className="flex-1 relative">
                  <input
                    ref={subTaskMessageInputRef}
                    type="text"
                    value={newSubTaskMessage}
                    onChange={(e) => handleMessageInputChange(e.target.value, true)}
                    onKeyDown={(e) => {
                      if (showMentionList && e.key === 'ArrowDown') {
                        e.preventDefault();
                      } else if (showMentionList && e.key === 'Enter') {
                        e.preventDefault();
                        const mentionable = getMentionableEmployees();
                        const filtered = mentionable.filter(e => 
                          `${e.firstName} ${e.lastName}`.toLowerCase().includes(mentionQuery)
                        );
                        if (filtered.length > 0) {
                          insertMention(filtered[0], true);
                        }
                      }
                    }}
                    placeholder={replyingToSubTask ? "Type your reply..." : "Type your message..."}
                    className="w-full px-4 py-2 border border-neutral-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    disabled={sendingSubTaskMessage || isRecordingSubTask}
                  />
                  {/* Mention Autocomplete */}
                  {showMentionList && showSubTaskChat && (
                    <div 
                      className="mention-autocomplete absolute bottom-full left-0 mb-2 w-64 bg-white border border-neutral-200 rounded-lg shadow-lg z-50 max-h-48 overflow-y-auto"
                      style={{ bottom: '100%', marginBottom: '8px' }}
                    >
                      {getMentionableEmployees()
                        .filter(e => 
                          `${e.firstName} ${e.lastName}`.toLowerCase().includes(mentionQuery) ||
                          e.email.toLowerCase().includes(mentionQuery)
                        )
                        .map((employee) => (
                          <button
                            key={employee.id}
                            type="button"
                            onClick={() => insertMention(employee, true)}
                            className="w-full px-3 py-2 text-left hover:bg-blue-50 flex items-center gap-2"
                          >
                            {employee.profileImageUrl && getProfileImageUrl(employee.profileImageUrl, employee.updatedAt) ? (
                              <img src={getProfileImageUrl(employee.profileImageUrl, employee.updatedAt)!} alt={`${employee.firstName} ${employee.lastName}`} className="w-8 h-8 rounded-full object-cover" />
                            ) : (
                              <div className="w-8 h-8 rounded-full bg-blue-600 flex items-center justify-center text-white text-sm font-medium">
                                {employee.firstName?.[0] || employee.lastName?.[0] || 'U'}
                              </div>
                            )}
                            <div>
                              <div className="text-sm font-medium text-neutral-900">
                                {employee.firstName} {employee.lastName}
                              </div>
                              <div className="text-xs text-neutral-500">{employee.email}</div>
                            </div>
                          </button>
                        ))}
                      {getMentionableEmployees().filter(e => 
                        `${e.firstName} ${e.lastName}`.toLowerCase().includes(mentionQuery) ||
                        e.email.toLowerCase().includes(mentionQuery)
                      ).length === 0 && (
                        <div className="px-3 py-2 text-sm text-neutral-500">No matches</div>
                      )}
                    </div>
                  )}
                </div>
                <button
                  type="submit"
                  disabled={(!newSubTaskMessage.trim() && selectedSubTaskFiles.length === 0) || sendingSubTaskMessage || isRecordingSubTask}
                  className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                >
                  <Send className="h-4 w-4" />
                  {sendingSubTaskMessage ? 'Sending...' : 'Send'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Chat Modal */}
      {showChat && selectedTask && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-3xl w-full h-[80vh] flex flex-col">
            <div className="p-4 border-b border-neutral-200 flex justify-between items-center">
              <div>
                <h2 className="text-lg font-bold text-neutral-800">{selectedTask.name}</h2>
                <p className="text-sm text-neutral-500">Task Conversations</p>
              </div>
              <button
                onClick={() => {
                  setShowChat(false);
                  setSelectedTask(null); // Clear selected task to prevent subtask panel from auto-opening
                }}
                className="text-neutral-400 hover:text-neutral-600"
              >
                <X className="h-5 w-5" />
              </button>
            </div>

            {/* Chat Messages */}
            <div 
              ref={chatScrollContainerRef}
              className="flex-1 overflow-y-auto p-4 space-y-4 bg-neutral-50"
            >
              {loadingConversations ? (
                <div className="text-center text-neutral-500 py-8">
                  Loading conversations...
                </div>
              ) : selectedTask.conversations && selectedTask.conversations.length > 0 ? (
                selectedTask.conversations.map((conv) => {
                  const hasMessage = conv.message && conv.message.trim();
                  const hasAttachments = conv.attachments && conv.attachments.length > 0;
                  
                  // Comprehensive debug logging
                  console.log(`[Render] Rendering conversation ${conv.id}:`, {
                    hasMessage,
                    messageLength: conv.message?.length || 0,
                    hasAttachments,
                    attachmentsValue: conv.attachments,
                    attachmentsType: typeof conv.attachments,
                    attachmentsIsArray: Array.isArray(conv.attachments),
                    attachmentsCount: conv.attachments?.length || 0,
                    attachmentsDetails: conv.attachments
                  });
                  
                  if (conv.attachments) {
                    if (Array.isArray(conv.attachments)) {
                      console.log(`[Render] ✅ Conversation ${conv.id} has ${conv.attachments.length} attachments (array)`);
                      conv.attachments.forEach((att: any, idx: number) => {
                        console.log(`[Render]   Attachment ${idx + 1}:`, {
                          id: att.id,
                          fileName: att.fileName,
                          filePath: att.filePath,
                          fileSize: att.fileSize
                        });
                      });
                    } else {
                      console.warn(`[Render] ⚠️ Conversation ${conv.id} attachments is not an array:`, typeof conv.attachments, conv.attachments);
                    }
                  } else {
                    console.log(`[Render] Conversation ${conv.id} has no attachments property or it's null/undefined`);
                  }
                  
                  return (
                    <div 
                      key={conv.id} 
                      ref={(el) => { if (el) conversationRefs.current[conv.id] = el; }}
                      className="flex gap-3 group"
                    >
                    {(() => {
                      const emp = conv.employee;
                      const empProfileUrl = emp && getProfileImageUrl((emp as { profileImageUrl?: string }).profileImageUrl ?? employees.find(e => e.id === emp.id)?.profileImageUrl, (emp as { updatedAt?: string }).updatedAt ?? employees.find(e => e.id === emp.id)?.updatedAt);
                      return empProfileUrl ? (
                        <img src={empProfileUrl} alt={`${emp.firstName} ${emp.lastName}`} className="h-8 w-8 rounded-full object-cover flex-shrink-0" />
                      ) : (
                        <div className="h-8 w-8 bg-blue-600 rounded-full flex items-center justify-center text-white text-sm font-medium flex-shrink-0">
                          {emp?.firstName?.[0] || 'U'}
                        </div>
                      );
                    })()}
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-1">
                        <span className="text-sm font-semibold text-neutral-900">
                          {conv.employee?.firstName} {conv.employee?.lastName}
                        </span>
                        <span className="text-xs text-neutral-500">
                          {formatCompanyDateTime(conv.createdAt || conv.timestamp || '')}
                        </span>
                      </div>
                      
                      {/* Reply To Preview */}
                      {conv.replyTo && (
                        <div 
                          className="mb-2 ml-4 pl-3 border-l-2 border-blue-400 bg-neutral-100 rounded p-2 text-xs cursor-pointer hover:bg-neutral-200 transition-colors"
                          onClick={(e) => {
                            e.stopPropagation();
                            const originalConvId = conv.replyTo?.id;
                            if (originalConvId) {
                              // Wait a bit for DOM to be ready, then scroll
                              setTimeout(() => {
                                const originalElement = subTaskConversationRefs.current[originalConvId];
                                if (originalElement) {
                                  originalElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
                                  // Highlight briefly
                                  originalElement.classList.add('ring-2', 'ring-blue-400');
                                  setTimeout(() => {
                                    originalElement.classList.remove('ring-2', 'ring-blue-400');
                                  }, 2000);
                                } else {
                                  console.warn('[Reply Preview] Original conversation element not found:', originalConvId);
                                }
                              }, 100);
                            }
                          }}
                        >
                          <div className="font-semibold text-neutral-800 mb-1">
                            {conv.replyTo.employee.firstName} {conv.replyTo.employee.lastName}
                          </div>
                          <div className="text-neutral-600 line-clamp-2">{conv.replyTo.message}</div>
                        </div>
                      )}
                      
                      {((conv.message && conv.message.trim()) || (conv.attachments && conv.attachments.length > 0)) && (
                        <div className="bg-white rounded-lg p-3 shadow-sm border border-neutral-200 relative">
                          {conv.message && conv.message.trim() && (
                            <p className="text-sm text-neutral-700 whitespace-pre-wrap">{conv.message}</p>
                          )}
                          
                          {/* Attachments */}
                          {conv.attachments && conv.attachments.length > 0 && (
                            <div className={`space-y-2 ${conv.message && conv.message.trim() ? 'mt-2' : ''}`}>
                              {conv.attachments.map((attachment) => {
                                const isAudio = attachment.contentType?.startsWith('audio/') || 
                                                attachment.fileName?.endsWith('.webm') || 
                                                attachment.fileName?.endsWith('.mp3') || 
                                                attachment.fileName?.endsWith('.wav') ||
                                                attachment.fileName?.endsWith('.m4a') ||
                                                attachment.fileName?.endsWith('.aac') ||
                                                attachment.fileName?.startsWith('voice-message-') ||
                                                attachment.fileName?.startsWith('task_admin_chat_');
                                
                                if (isAudio) {
                                  return (
                                    <div
                                      key={attachment.id}
                                      className="flex items-center gap-2 p-2 bg-neutral-50 border border-neutral-200 rounded"
                                    >
                                      <Mic className="h-4 w-4 text-purple-600 flex-shrink-0" />
                                      <ConversationVoiceAttachment
                                        openHref={getAttachmentUrl(
                                          attachment.fileUrl ?? attachment.filePath,
                                          'task-conversations'
                                        )}
                                        playbackUrl={
                                          selectedTask
                                            ? getTaskConversationAttachmentPlaybackUrl(
                                                selectedTask.id,
                                                conv.id,
                                                attachment.id
                                              )
                                            : null
                                        }
                                        fileName={attachment.fileName}
                                      />
                                    </div>
                                  );
                                }
                                
                                return (
                                  <a
                                    key={attachment.id}
                                    href={getAttachmentUrl(
                                      attachment.fileUrl ?? attachment.filePath,
                                      'task-conversations'
                                    )}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="flex items-center gap-2 p-2 bg-neutral-50 border border-neutral-200 rounded hover:bg-neutral-100 transition-colors"
                                  >
                                    <FileIcon className="h-4 w-4 text-blue-600 flex-shrink-0" />
                                    <div className="flex-1 min-w-0">
                                      <div className="text-sm font-medium text-neutral-900 truncate">
                                        {attachment.fileName}
                                      </div>
                                      <div className="text-xs text-neutral-500">
                                        {formatFileSize(attachment.fileSize)}
                                      </div>
                                    </div>
                                    <Download className="h-4 w-4 text-neutral-400 flex-shrink-0" />
                                  </a>
                                );
                              })}
                            </div>
                          )}
                        
                          {/* Reactions */}
                          {conv.reactions && conv.reactions.length > 0 && (
                            <div className="mt-2 flex flex-wrap gap-1">
                              {conv.reactions.map((reaction, idx) => (
                                <div key={idx} className="relative">
                                <button
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      setShowReactionDetails({
                                        conversationId: conv.id,
                                        reaction,
                                        isSubTask: false
                                      });
                                    }}
                                  className={`flex items-center gap-1 px-2 py-1 rounded-full text-xs border transition-colors ${
                                    reaction.employees.some(e => e.id === currentEmployee?.id)
                                      ? 'bg-blue-50 border-blue-300 text-blue-700'
                                      : 'bg-neutral-50 border-neutral-200 text-neutral-700 hover:bg-neutral-100'
                                  }`}
                                >
                                  <span>{getReactionEmoji(reaction.type)}</span>
                                  <span>{reaction.count}</span>
                                </button>
                                </div>
                              ))}
                            </div>
                          )}
                          
                          {/* Action Buttons */}
                          <div className="mt-2 flex items-center gap-2 opacity-0 group-hover:opacity-100 transition-opacity">
                            <button
                              onClick={() => setReplyingTo(conv)}
                              className="flex items-center gap-1 px-2 py-1 text-xs text-neutral-600 hover:text-blue-600 hover:bg-blue-50 rounded transition-colors"
                            >
                              <Reply className="h-3 w-3" />
                              Reply
                            </button>
                            <div className="relative">
                              <button
                                onClick={() => setShowReactionPicker(showReactionPicker === conv.id ? null : conv.id)}
                                className="flex items-center gap-1 px-2 py-1 text-xs text-neutral-600 hover:text-blue-600 hover:bg-blue-50 rounded transition-colors"
                              >
                                <Smile className="h-3 w-3" />
                                React
                              </button>
                              
                              {/* Reaction Picker */}
                              {showReactionPicker === conv.id && (
                                <div className="absolute bottom-full left-0 mb-2 bg-white border border-neutral-200 rounded-lg shadow-lg p-2 flex gap-1 z-10">
                                  {['like', 'love', 'laugh', 'wow', 'sad', 'angry'].map((type) => (
                                    <button
                                      key={type}
                                      onClick={() => handleReaction(conv.id, type)}
                                      className="p-2 hover:bg-neutral-100 rounded transition-colors text-lg"
                                      title={type.charAt(0).toUpperCase() + type.slice(1)}
                                    >
                                      {getReactionEmoji(type)}
                                    </button>
                                  ))}
                                </div>
                              )}
                            </div>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                  );
                })
              ) : (
                <div className="text-center text-neutral-500 py-8">
                  No conversations yet. Start the conversation!
                </div>
              )}
              <div ref={chatEndRef} />
            </div>

            {/* Chat Input */}
            <form onSubmit={handleSendMessage} className="p-4 border-t border-neutral-200 bg-white">
              {/* Reply To Preview */}
              {replyingTo && (
                <div className="mb-2 p-2 bg-blue-50 border-l-4 border-blue-400 rounded-md flex items-start gap-2">
                  <div 
                    className="flex-1 cursor-pointer"
                    onClick={(e) => {
                      e.stopPropagation();
                      const originalConvId = replyingTo.id;
                      if (originalConvId) {
                        // Wait a bit for DOM to be ready, then scroll
                        setTimeout(() => {
                          const originalElement = conversationRefs.current[originalConvId];
                          const scrollContainer = chatScrollContainerRef.current;
                          if (originalElement && scrollContainer) {
                            // Calculate position relative to scroll container
                            const containerRect = scrollContainer.getBoundingClientRect();
                            const elementRect = originalElement.getBoundingClientRect();
                            const scrollTop = scrollContainer.scrollTop;
                            const elementTop = elementRect.top - containerRect.top + scrollTop;
                            
                            // Scroll to center the element
                            scrollContainer.scrollTo({
                              top: elementTop - (scrollContainer.clientHeight / 2) + (elementRect.height / 2),
                              behavior: 'smooth'
                            });
                            
                            // Highlight briefly
                            originalElement.classList.add('ring-2', 'ring-blue-400');
                            setTimeout(() => {
                              originalElement.classList.remove('ring-2', 'ring-blue-400');
                            }, 2000);
                          } else if (originalElement) {
                            // Fallback to scrollIntoView
                            originalElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
                          } else {
                            console.warn('[Reply Preview] Original conversation element not found:', originalConvId);
                          }
                        }, 100);
                      }
                    }}
                  >
                    <div className="text-xs font-semibold text-blue-800 mb-1">
                      {replyingTo.employee?.firstName} {replyingTo.employee?.lastName}
                    </div>
                    <div className="text-xs text-neutral-600 line-clamp-2">{replyingTo.message}</div>
                  </div>
                  <button
                    type="button"
                    onClick={() => setReplyingTo(null)}
                    className="text-neutral-400 hover:text-neutral-600 flex-shrink-0"
                  >
                    <X className="h-4 w-4" />
                  </button>
                </div>
              )}

              {/* Selected Files Preview */}
              {selectedFiles.length > 0 && (
                <div className="mb-2 flex flex-wrap gap-2">
                  {selectedFiles.map((file, index) => (
                    <div
                      key={index}
                      className="flex items-center gap-2 px-3 py-2 bg-blue-50 border border-blue-200 rounded-md"
                    >
                      {file.name.startsWith('voice-message-') || file.type.startsWith('audio/') ? (
                        <Mic className="h-4 w-4 text-purple-600 flex-shrink-0" />
                      ) : (
                        <FileIcon className="h-4 w-4 text-blue-600 flex-shrink-0" />
                      )}
                      <span className="text-sm text-neutral-700 truncate max-w-[200px]">
                        {file.name.startsWith('voice-message-') ? 'Voice Message' : file.name}
                      </span>
                      <span className="text-xs text-neutral-500">
                        {formatFileSize(file.size)}
                      </span>
                      <button
                        type="button"
                        onClick={() => removeFile(index)}
                        className="text-neutral-400 hover:text-red-600 flex-shrink-0"
                      >
                        <X className="h-4 w-4" />
                      </button>
                    </div>
                  ))}
                </div>
              )}
              
              <div className="flex gap-2">
                <input
                  ref={fileInputRef}
                  type="file"
                  multiple
                  onChange={handleFileSelect}
                  className="hidden"
                  id="file-input"
                />
                <label
                  htmlFor="file-input"
                  className="px-3 py-2 border border-neutral-300 rounded-md hover:bg-neutral-50 cursor-pointer flex items-center justify-center transition-colors"
                  title="Attach files"
                >
                  <Paperclip className="h-5 w-5 text-neutral-600" />
                </label>
                {isRecording ? (
                  <button
                    type="button"
                    onClick={stopRecording}
                    className="px-3 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 flex items-center gap-2"
                    title="Stop recording"
                  >
                    <Square className="h-4 w-4" />
                    <span className="text-sm">{formatRecordingTime(recordingTime)}</span>
                  </button>
                ) : (
                  <button
                    type="button"
                    onClick={startRecording}
                    className="px-3 py-2 border border-neutral-300 rounded-md hover:bg-neutral-50 flex items-center justify-center transition-colors"
                    title="Record voice message"
                  >
                    <Mic className="h-5 w-5 text-purple-600" />
                  </button>
                )}
                <div className="flex-1 relative">
                <input
                    ref={messageInputRef}
                  type="text"
                  value={newMessage}
                    onChange={(e) => handleMessageInputChange(e.target.value, false)}
                    onKeyDown={(e) => {
                      if (showMentionList && e.key === 'ArrowDown') {
                        e.preventDefault();
                        // Handle arrow key navigation
                      } else if (showMentionList && e.key === 'Enter') {
                        e.preventDefault();
                        const mentionable = getMentionableEmployees();
                        const filtered = mentionable.filter(e => 
                          `${e.firstName} ${e.lastName}`.toLowerCase().includes(mentionQuery)
                        );
                        if (filtered.length > 0) {
                          insertMention(filtered[0], false);
                        }
                      }
                    }}
                  placeholder={replyingTo ? "Type your reply..." : "Type your message..."}
                    className="w-full px-4 py-2 border border-neutral-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  disabled={sendingMessage || isRecording}
                />
                  {/* Mention Autocomplete */}
                  {showMentionList && !showSubTaskChat && (
                    <div 
                      className="mention-autocomplete absolute bottom-full left-0 mb-2 w-64 bg-white border border-neutral-200 rounded-lg shadow-lg z-50 max-h-48 overflow-y-auto"
                      style={{ bottom: '100%', marginBottom: '8px' }}
                    >
                      {getMentionableEmployees()
                        .filter(e => 
                          `${e.firstName} ${e.lastName}`.toLowerCase().includes(mentionQuery) ||
                          e.email.toLowerCase().includes(mentionQuery)
                        )
                        .map((employee) => (
                          <button
                            key={employee.id}
                            type="button"
                            onClick={() => insertMention(employee, false)}
                            className="w-full px-3 py-2 text-left hover:bg-blue-50 flex items-center gap-2"
                          >
                            {employee.profileImageUrl && getProfileImageUrl(employee.profileImageUrl, employee.updatedAt) ? (
                              <img src={getProfileImageUrl(employee.profileImageUrl, employee.updatedAt)!} alt={`${employee.firstName} ${employee.lastName}`} className="w-8 h-8 rounded-full object-cover" />
                            ) : (
                              <div className="w-8 h-8 rounded-full bg-blue-600 flex items-center justify-center text-white text-sm font-medium">
                                {employee.firstName?.[0] || employee.lastName?.[0] || 'U'}
                              </div>
                            )}
                            <div>
                              <div className="text-sm font-medium text-neutral-900">
                                {employee.firstName} {employee.lastName}
                              </div>
                              <div className="text-xs text-neutral-500">{employee.email}</div>
                            </div>
                          </button>
                        ))}
                      {getMentionableEmployees().filter(e => 
                        `${e.firstName} ${e.lastName}`.toLowerCase().includes(mentionQuery) ||
                        e.email.toLowerCase().includes(mentionQuery)
                      ).length === 0 && (
                        <div className="px-3 py-2 text-sm text-neutral-500">No matches</div>
                      )}
                    </div>
                  )}
                </div>
                <button
                  type="submit"
                  disabled={(!newMessage.trim() && selectedFiles.length === 0) || sendingMessage || isRecording}
                  className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                >
                  <Send className="h-4 w-4" />
                  {sendingMessage ? 'Sending...' : 'Send'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Reaction Details Popup */}
      {showReactionDetails && (
        <div 
          className="fixed inset-0 bg-black bg-opacity-30 flex items-center justify-center z-[60]"
          onClick={() => setShowReactionDetails(null)}
        >
          <div 
            className="bg-white rounded-lg shadow-xl max-w-sm w-full mx-4"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="p-4 border-b border-neutral-200 flex justify-between items-center">
              <div className="flex items-center gap-2">
                <span className="text-2xl">{getReactionEmoji(showReactionDetails.reaction.type)}</span>
                <h3 className="text-lg font-semibold text-neutral-800">
                  {showReactionDetails.reaction.type.charAt(0).toUpperCase() + showReactionDetails.reaction.type.slice(1)}
                </h3>
              </div>
              <button
                onClick={() => setShowReactionDetails(null)}
                className="text-neutral-400 hover:text-neutral-600"
              >
                <X className="h-5 w-5" />
              </button>
            </div>
            <div className="p-4 max-h-96 overflow-y-auto">
              {showReactionDetails.reaction.employees.length > 0 ? (
                <div className="space-y-2">
                  {showReactionDetails.reaction.employees.map((employee) => {
                    const empProfileUrl = getProfileImageUrl((employee as { profileImageUrl?: string }).profileImageUrl ?? employees.find(e => e.id === employee.id)?.profileImageUrl, (employee as { updatedAt?: string }).updatedAt ?? employees.find(e => e.id === employee.id)?.updatedAt);
                    return (
                    <div key={employee.id} className="flex items-center gap-3 p-2 hover:bg-neutral-50 rounded">
                      {empProfileUrl ? (
                        <img src={empProfileUrl} alt={`${employee.firstName} ${employee.lastName}`} className="w-10 h-10 rounded-full object-cover" />
                      ) : (
                        <div className="w-10 h-10 rounded-full bg-blue-600 flex items-center justify-center text-white font-medium">
                          {employee.firstName?.[0]}{employee.lastName?.[0]}
                        </div>
                      )}
                      <div className="flex-1">
                        <div className="font-medium text-neutral-900">
                          {employee.firstName} {employee.lastName}
                        </div>
                      </div>
                    </div>
                  );})}
                </div>
              ) : (
                <div className="text-center text-neutral-500 py-4">
                  No one has reacted yet
                </div>
              )}
            </div>
            <div className="p-4 border-t border-neutral-200 flex justify-end">
              <button
                onClick={() => {
                  if (showReactionDetails.isSubTask && selectedSubTask) {
                    handleSubTaskReaction(showReactionDetails.conversationId, showReactionDetails.reaction.type);
                  } else if (selectedTask) {
                    handleReaction(showReactionDetails.conversationId, showReactionDetails.reaction.type);
                  }
                  setShowReactionDetails(null);
                }}
                className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
              >
                {showReactionDetails.reaction.employees.some(e => e.id === currentEmployee?.id) 
                  ? 'Remove Reaction' 
                  : 'Add Reaction'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Position / schedule → employees in this assignment (scoped to task location when set) */}
      {assignmentEmployeesModal && (
        <div
          className="fixed inset-0 z-[70] flex items-center justify-center bg-black/50 p-4"
          role="presentation"
          onClick={() => setAssignmentEmployeesModal(null)}
        >
          <div
            className="bg-white rounded-lg shadow-xl max-w-md w-full max-h-[min(85vh,520px)] overflow-hidden flex flex-col"
            role="dialog"
            aria-modal="true"
            aria-labelledby="assignment-modal-title"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="px-4 py-3 border-b border-neutral-200 flex justify-between items-start gap-2">
              <div>
                <h3 id="assignment-modal-title" className="text-lg font-semibold text-neutral-900">
                  {assignmentEmployeesModal.kind === 'position' ? 'Position' : 'Schedule'}: {assignmentEmployeesModal.label}
                </h3>
                <p className="text-xs text-neutral-500 mt-1">
                  Active employees who match this {assignmentEmployeesModal.kind} on their profile
                  {assignmentEmployeesModal.loading ? '' : ` (${assignmentEmployeesModal.employees.length})`}
                </p>
              </div>
              <button
                type="button"
                onClick={() => setAssignmentEmployeesModal(null)}
                className="p-1 rounded-md text-neutral-500 hover:bg-neutral-100 hover:text-neutral-800 shrink-0"
                aria-label="Close"
              >
                <X className="h-5 w-5" />
              </button>
            </div>
            <div className="px-4 py-3 overflow-y-auto flex-1 min-h-0">
              {assignmentEmployeesModal.loading ? (
                <div className="flex flex-col items-center justify-center py-12 gap-2 text-neutral-500">
                  <Loader2 className="h-8 w-8 animate-spin text-blue-600" />
                  <span className="text-sm">Loading employees…</span>
                </div>
              ) : assignmentEmployeesModal.employees.length === 0 ? (
                <p className="text-sm text-neutral-600 py-4">
                  No active employees found for this assignment. If the task has a location, only employees linked to that location are listed.
                </p>
              ) : (
                <ul className="divide-y divide-neutral-100">
                  {assignmentEmployeesModal.employees.map((emp) => {
                    const url = getProfileImageUrl(emp.profileImageUrl, emp.updatedAt);
                    const initials = `${emp.firstName?.[0] || ''}${emp.lastName?.[0] || ''}`.toUpperCase();
                    return (
                      <li key={emp.id} className="flex items-center gap-3 py-2.5">
                        {url ? (
                          <img src={url} alt="" className="w-9 h-9 rounded-full object-cover shrink-0" />
                        ) : (
                          <div className="w-9 h-9 rounded-full bg-blue-500 text-white flex items-center justify-center text-xs font-semibold shrink-0">
                            {initials}
                          </div>
                        )}
                        <div className="min-w-0">
                          <div className="text-sm font-medium text-neutral-900 truncate">
                            {emp.firstName} {emp.lastName}
                          </div>
                          {emp.email && <div className="text-xs text-neutral-500 truncate">{emp.email}</div>}
                        </div>
                      </li>
                    );
                  })}
                </ul>
              )}
            </div>
            <div className="px-4 py-3 border-t border-neutral-200 flex justify-end">
              <button
                type="button"
                onClick={() => setAssignmentEmployeesModal(null)}
                className="px-4 py-2 text-sm font-medium text-neutral-700 bg-neutral-100 rounded-md hover:bg-neutral-200"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {inactiveAssigneesDialog ? (
        <InactiveAssigneesDialog
          inactive={inactiveAssigneesDialog.inactive}
          onResolve={(choice) => {
            inactiveAssigneesDialog.resolve?.(choice);
            setInactiveAssigneesDialog(null);
          }}
        />
      ) : null}
    </div>
  );
};

export default TaskAdmin;

